// fallow-ignore-file code-duplication
import { Route as workbooksRoute } from './pages/authenticated/workbooks';
import { Route as workbookDetailRoute } from './pages/authenticated/workbooks/$workbookId';
import { Route as workbookLibraryReportRoute } from './pages/authenticated/workbooks/reports/$reportId';
import { msg } from 'gt-react';
import { lazyRouteComponent } from '@tanstack/react-router';
import {
  isBooleanFeatureFlagEnabled,
  waitForFeatureFlags,
} from '@/integrations/feature-flags/feature-flags';
import type { AnyRoute } from '@tanstack/react-router';

// Route-specific components defined via file-based routes (keep static)
import { compareCsvsSearchSchema } from './lib/csv-compare/compareSearch';
import { validateInvoicesSearch } from './pages/authenticated/accounts-payable/invoices/invoicesSearch';
import { Route as FamousEDIShipmentDetailRoute } from 'src/pages/authenticated/famous-edi-shipments/$shipmentId';
import { Route as OrderConfirmationDetailRoute } from 'src/pages/authenticated/famous-edi-order-confirmation/$orderConfirmationId';
import { Route as PriceQuoteSheetDashboardDetailRoute } from './pages/authenticated/price-sheet-dashboard/detail.$quoteSheetId';
import { Route as PriceQuoteSheetInboxDetailRoute } from './pages/authenticated/price-sheet-inbox/$quoteSheetId';
import { Route as memoryBankNotesRoute } from './pages/authenticated/memory-bank-notes';
import { Route as workflowMemoriesRoute } from './pages/authenticated/(workflow-detail)/memories';
import { Route as workflowActivityRoute } from './pages/authenticated/(workflow-detail)/activity';
import { Route as allWorkflowRunsRoute } from './pages/authenticated/(workflows-list)/runs';
import { Route as guardrailPoliciesRoute } from './pages/authenticated/guardrail-policies';
import { Route as integrationsListRoute } from './pages/authenticated/integrations';
import { Route as auditReportsRoute } from './pages/authenticated/audit-reports';
import { Route as pricingMasterlistRoute } from './pages/authenticated/pricing/masterlist';
import { Route as pricingResultsToUploadRoute } from './pages/authenticated/pricing/results-to-upload';
import { Route as pricingVendorPortalsRoute } from './pages/authenticated/pricing/vendor-portals';
import { Route as pricingSupplierPortalsRedirectRoute } from './pages/authenticated/pricing/supplier-portals';
import { Route as notificationsHistoryRoute } from './pages/authenticated/notifications';
import { Route as arTasksRoute } from './pages/authenticated/accounts-receivable/tasks';
import { Route as arCashMetricsRoute } from './pages/authenticated/accounts-receivable/cash-metrics';
import { Route as arCheckDepositsRoute } from './pages/authenticated/accounts-receivable/check-deposits';
import { Route as arCheckDepositDetailRoute } from './pages/authenticated/accounts-receivable/check-deposits/$depositId';
import { Route as appsGalleryRoute } from './pages/authenticated/apps/gallery';
import { Route as arCashDashboardRoute } from './pages/authenticated/accounts-receivable/cash-dashboard';
import { Route as appsRoute } from './pages/authenticated/apps';
import { Route as appRoute } from './pages/authenticated/apps/$appId';
import { Route as reportAppRoute } from './pages/authenticated/reports/$appId';
import { Route as pricelistCaseDetailRoute } from './pages/authenticated/item-master/pricelist-cases/$caseId';
import { PricelistCasesSearchSchema } from './pages/authenticated/item-master/components/pricelistCaseListState';
import { PricelistHistorySearchSchema } from './pages/authenticated/item-master/components/pricelistHistoryListState';
import { ReviewPageSearchSchema } from './pages/authenticated/item-master/components/review-mode/reviewModeSearch';
import { Route as agentDefinitionVersionsTabRoute } from './pages/authenticated/(agent-detail)/versions';
import { Route as procedureVersionsRoute } from './pages/authenticated/(procedure-detail)/procedures/$id/versions';
import { Route as procedureVersionDetailRoute } from './pages/authenticated/(procedure-detail)/procedures/$id/v/$versionNumber';
import { Route as backgroundAgentProcedureImportReviewRoute } from './pages/authenticated/procedures/imports/$importId';
import { Route as aiUsageRoute } from './pages/authenticated/ai-usage';
import { Route as workflowAdminRoute } from './pages/authenticated/workflow-admin';
import { Route as welcomeRoute } from './pages/authenticated/welcome';
import { Route as homeRoute } from './pages/authenticated/home';
import { Route as chatRoute } from './pages/authenticated/chat';
import { Route as feedbackRoute } from './pages/authenticated/feedback';
import { Route as apCommandCenterRoute } from './pages/authenticated/accounts-payable/command-center';
import { Route as apMetricsRoute } from './pages/authenticated/accounts-payable/metrics';
import { Route as apBulkPaymentsRoute } from './pages/authenticated/accounts-payable/bulk-payments';
import { Route as apThroughputRedirectRoute } from './pages/authenticated/accounts-payable/throughput';
import { Route as apExceptionRulesRoute } from './pages/authenticated/accounts-payable/exception-rules';
import { Route as apExceptionRuleSetCreateRoute } from './pages/authenticated/accounts-payable/exception-rules/new';
import { Route as apExceptionRuleSetDetailRoute } from './pages/authenticated/accounts-payable/exception-rules/$ruleSetId';
import { Route as apExceptionRuleSetEditRoute } from './pages/authenticated/accounts-payable/exception-rules/$ruleSetId/edit';
import { Route as featureFlagsRoute } from './pages/authenticated/ff_overrides';
import { Route as userSessionsRoute } from './pages/authenticated/user-sessions';
import { Route as feedbackTriageRoute } from './pages/authenticated/feedback-triage';
import { Route as backfillsRoute } from './pages/authenticated/admin/backfills';
import {
  Route as maintenanceRoute,
  MaintenanceIndexRoute as maintenanceIndexRoute,
  MaintenanceBackfillsRoute as maintenanceBackfillsRoute,
  MaintenanceIndexesRoute as maintenanceIndexesRoute,
  MaintenanceLargeTablesRoute as maintenanceLargeTablesRoute,
  MaintenanceWorkerScalingRoute as maintenanceWorkerScalingRoute,
  MaintenanceReconciliationRoute as maintenanceReconciliationRoute,
  MaintenanceSchemaDriftRedirectRoute as maintenanceSchemaDriftRedirectRoute,
} from './pages/authenticated/admin/maintenance/route';
import { Route as historicalImportRoute } from './pages/authenticated/admin/historical-import';
import { Route as customersRoute } from './pages/authenticated/customers';
import { Route as reviewThreadLinkRoute } from './pages/authenticated/item-master/review-threads/$threadId';
import { Route as suppliersRoute } from './pages/authenticated/suppliers';
import { Route as supplyNetworkRoute } from './pages/authenticated/inventory-management/supply-network';
import { Route as orderMetricsRoute } from './pages/authenticated/order-metrics';
import { Route as salesReportsRoute } from './pages/authenticated/sales-reports';
import { Route as famousEDIRoute } from './pages/authenticated/famous-edi';
import { Route as salesOrderStatusRoute } from './pages/authenticated/procurement/sales-order-status';
import { Route as transferOrderStatusRoute } from './pages/authenticated/procurement/transfer-order-status';
import { OrderStatusNavItem } from './pages/authenticated/procurement/OrderStatusNavItem';
import { Route as as400RawDataRoute } from './pages/authenticated/procurement/as400-raw-data';
import { Route as queryBuilderRoute } from './pages/authenticated/procurement/query-builder';
import { Route as obsoleteProductsRoute } from './pages/authenticated/procurement/obsolete-products';
import { Route as inboxRoute } from './pages/authenticated/inbox';
import { Route as documentsRoute } from './pages/authenticated/documents';
import { Route as quoteBuilderRoute } from './pages/authenticated/quote-builder';
import { Route as productCatalogRoute } from './pages/authenticated/product-catalog';
import { Route as customerProductMatchesRoute } from './pages/authenticated/customer-product-matches';
import { Route as supplierProductMatchesRoute } from './pages/authenticated/supplier-product-matches';
import { Route as priceListsRoute } from './pages/authenticated/price-lists';
import { Route as agentMonitorListingRoute } from './pages/authenticated/agent-monitor';
import { Route as sharedLinksRoute } from './pages/authenticated/shared-links';
import { Route as shipAndDebitExecutiveSummaryRoute } from './pages/authenticated/ship-and-debit/executive-summary';
import { Route as shipAndDebitNoClaimRoute } from './pages/authenticated/ship-and-debit/no-claim-transactions';
import { Route as shipAndDebitStagedInternalRoute } from './pages/authenticated/ship-and-debit/staged-internal';
import { Route as shipAndDebitSearchTransactionsRoute } from './pages/authenticated/ship-and-debit/search-transactions';
import { Route as shipAndDebitSampleLibraryRoute } from './pages/authenticated/ship-and-debit/sample-library';
import { Route as shipAndDebit849ReviewRoute } from './pages/authenticated/ship-and-debit/849-review';
import { Route as shipAndDebitConfigAuthoringRoute } from './pages/authenticated/ship-and-debit/config-authoring';
import { Route as shipAndDebitActivityLogRoute } from './pages/authenticated/ship-and-debit/activity-log';
import { Route as shipAndDebitEdi997AcknowledgementsRoute } from './pages/authenticated/ship-and-debit/edi-997-acknowledgements';
import { Route as shipAndDebitCompanyLocationsRoute } from './pages/authenticated/ship-and-debit/company-locations';
import { Route as shipAndDebitRexelFilesRoute } from './pages/authenticated/ship-and-debit/rexel-files';
import { Route as shipAndDebitSuppliersRoute } from './pages/authenticated/ship-and-debit/suppliers';
import { Route as shipAndDebitDashboardRoute } from './pages/authenticated/ship-and-debit';
import { Route as shipAndDebit844And849Route } from './pages/authenticated/ship-and-debit/844-submissions';
import { Route as shipAndDebit849ResponsesRoute } from './pages/authenticated/ship-and-debit/849-responses';
import { Route as shipAndDebit849ResponseDetailRoute } from './pages/authenticated/ship-and-debit/849-responses/$responseId';
import { Route as shipAndDebitAdminReportsRoute } from './pages/authenticated/ship-and-debit/reports';
import { Route as shipAndDebitSearch844Route } from './pages/authenticated/ship-and-debit/search-rexel-files';
import { Route as shipAndDebitStaged844Route } from './pages/authenticated/ship-and-debit/staged';
import { Route as shipAndDebitSupplierDetailRoute } from './pages/authenticated/ship-and-debit/suppliers/$companyId';
import { Route as shipAndDebit844ConfigurationRoute } from './pages/authenticated/ship-and-debit/844-configuration';
import { Route as shipAndDebit844ConfigurationSupplierRoute } from './pages/authenticated/ship-and-debit/844-configuration/$companyId';
import { Route as shipAndDebitVDMsRoute } from './pages/authenticated/ship-and-debit/vdm';
import { Route as shipAndDebitVDMDetailRoute } from './pages/authenticated/ship-and-debit/vdm/$vdmId';
import { Route as ediTextMessagesRoute } from './pages/authenticated/edi-text-messages';
import { Route as orderfulTransactionsRoute } from './pages/authenticated/orderful-transactions';
import { Route as controlPlaneLinkRoute } from './pages/authenticated/settings/control-plane-link';
import { Route as schemaLabelingRoute } from './pages/authenticated/settings/organization/schema-definitions/labeling';
import { Route as rexelMonitoringRoute } from './pages/authenticated/rexel-monitoring';
import { Route as settingsUserLanguageRoute } from './pages/authenticated/(settings-user)/language';
import {
  Route as settingsUserKeyboardShortcutsRoute,
  attachSettingsUserLayout,
} from './pages/authenticated/settings/user/keyboard-shortcuts';
// Sales Orders & Discounts — OMS v3 owns the unversioned paths; the legacy
// pages moved to /sales-orders-v2 and /discounts-v1, and the old versioned
// paths are temporary redirects. Route modules are co-located under
// src/pages; the page-component folders keep their original (versioned or
// unversioned) names — see the header comments in each module.
import { Route as salesOrdersRoute } from './pages/authenticated/sales-orders';
import { Route as salesOrderDetailRoute } from './pages/authenticated/sales-orders/$draftId';
import { Route as salesOrdersV2Route } from './pages/authenticated/sales-orders-v2';
import { Route as salesOrderV2Route } from './pages/authenticated/sales-orders-v2/$salesOrderId';
import { Route as enhancedSalesOrderDraftPageRoute } from './pages/authenticated/sales-orders-v2/draft/$salesOrderDraftId';
import { Route as salesOrdersV3RedirectRoute } from './pages/authenticated/sales-orders-v3';
import { Route as salesOrderV3DetailRedirectRoute } from './pages/authenticated/sales-orders-v3/$draftId';
import { Route as discountsRoute } from './pages/authenticated/discounts';
import { Route as discountsGroupReviewRoute } from './pages/authenticated/discounts/$groupId';
import { Route as discountsV1Route } from './pages/authenticated/discounts-v1';
import { Route as discountsV2RedirectRoute } from './pages/authenticated/discounts-v2';
import { Route as discountsV2GroupRedirectRoute } from './pages/authenticated/discounts-v2/$groupId';
import { Route as tasksQueueRoute } from './pages/authenticated/tasks';
import { Route as projectsRoute } from './pages/authenticated/projects';
import { Route as projectDetailRoute } from './pages/authenticated/projects/$projectId';
import { Route as taskDetailRoute } from './pages/authenticated/tasks/$taskId';
import { Route as namespacesRoute } from './pages/authenticated/namespaces';
import { Route as namespaceDetailRoute } from './pages/authenticated/namespaces/$namespaceId';
import { Route as taskPageQueueRoute } from './pages/authenticated/pages/$taskPageId';
import { Route as agentPageRoute } from './pages/authenticated/agent-pages/$agentPageId';
import { Route as taskTypeDetailRoute } from './pages/authenticated/settings/organization/task-types/$taskTypeId';
import { Route as taskTypeCreateRoute } from './pages/authenticated/settings/organization/task-types/new';
import { Route as integrationDetailRoute } from './pages/authenticated/integrations/$integrationKey';
import { Route as itemListsRoute } from './pages/authenticated/item-master/lists';
import { Route as dailyReportsRoute } from './pages/authenticated/item-master/utilities/daily-reports';
import { Route as eatonDspRoute } from './pages/authenticated/item-master/utilities/eaton-dsp';
import { Route as priceListBreakPivotRoute } from './pages/authenticated/item-master/utilities/price-list-break-pivot';
import { Route as millableReviewRoute } from './pages/authenticated/procurement/millable-review';
import { Route as overridesRoute } from './pages/authenticated/procurement/override-reviews';
import { Route as freightOptionsRoute } from './pages/authenticated/procurement/freight-options';
import { Route as forecastRunsLegacyRoute } from './pages/authenticated/procurement/forecast-runs';
import { Route as forecastSummaryRoute } from './pages/authenticated/procurement/forecast/summary';
import { Route as forecastRunHistoryRoute } from './pages/authenticated/procurement/forecast/run-history';
import { Route as forecastDetailsRoute } from './pages/authenticated/procurement/forecast/details';
import { Route as replenishmentDetailRoute } from './pages/authenticated/procurement/replenishment-detail';
import { Route as procurementFiltersRoute } from './pages/authenticated/procurement/filters/route';
import {
  Route as demandForecastingRoute,
  DemandForecastingIndexRoute as demandForecastingIndexRoute,
  DemandForecastingGeneralRoute as demandForecastingGeneralRoute,
  DemandForecastingOverridesRoute as demandForecastingOverridesRoute,
  DemandForecastingLeadTimeRoute as demandForecastingLeadTimeRoute,
  DemandForecastingSurplusRoute as demandForecastingSurplusRoute,
  DemandForecastingOutlierRulesRoute as demandForecastingOutlierRulesRoute,
  DemandForecastingSalesHistoryTransfersRoute as demandForecastingSalesHistoryTransfersRoute,
  DemandForecastingRunsRoute as demandForecastingRunsRoute,
} from './pages/authenticated/procurement/demand-forecasting/route';
import { Route as createTransferOrderRoute } from './pages/authenticated/procurement/internal-transfers/new';
import { Route as internalTransfersRoute } from './pages/authenticated/procurement/internal-transfers';
import { buyingBoardSearchSchema } from './pages/authenticated/procurement/replenishment/utils/buyingBoardSearch';
import { Route as createPurchaseOrderRoute } from './pages/authenticated/procurement/purchase-orders/new';
import {
  Route as validationsRoute,
  ValidationRuleDetailRoute as validationRuleDetailRoute,
} from './pages/authenticated/item-master/validations/route';
import { INBOUND_EMAIL_SORT_COLUMN_IDS } from './pages/authenticated/inbound-emails/inboundEmailSortColumns';
import { WORKFLOW_DEFINITION_SORT_FIELDS } from './pages/authenticated/workflows/workflowDefinitionListSearch';

// Lazy page components — only loaded when route is visited
const AgentEvalsPage = lazyRouteComponent(
  () => import('src/pages/authenticated/agent-evals/AgentEvalsPage'),
  'AgentEvalsPage',
);
const AgentEvalRunDetailPage = lazyRouteComponent(
  () => import('src/pages/authenticated/agent-evals/AgentEvalRunDetailPage'),
  'AgentEvalRunDetailPage',
);
const EvalsLayoutPage = lazyRouteComponent(
  () => import('src/pages/authenticated/evals/EvalsLayoutPage'),
  'EvalsLayoutPage',
);
const MockUiCatalogPage = lazyRouteComponent(
  () => import('src/pages/authenticated/evals/MockUiCatalogPage'),
  'MockUiCatalogPage',
);
const ChatHistoryPage = lazyRouteComponent(
  () => import('src/pages/authenticated/chat-agent/ChatHistoryPage'),
  'ChatHistoryPage',
);
const InboundEmailsPage = lazyRouteComponent(
  () => import('src/pages/authenticated/inbound-emails/InboundEmailsPage'),
);
const InboundEmailRulesPage = lazyRouteComponent(
  () => import('src/pages/authenticated/inbound-emails/InboundEmailRulesPage'),
  'InboundEmailRulesPage',
);
const DocumentDetailPage = lazyRouteComponent(
  () => import('src/pages/authenticated/documents/DocumentDetailPage'),
);
const FamousEDIShipmentsPage = lazyRouteComponent(
  () =>
    import('src/pages/authenticated/famous-edi-shipments/FamousEDIShipmentsPage'),
);
const FamousEDIOrderConfirmationPage = lazyRouteComponent(
  () =>
    import('src/pages/authenticated/famous-edi-order-confirmation/FamousEDIOrderConfirmationPage'),
);

const RebatesAnalyticsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/forecasting/RebatesAnalyticsPage'),
);
const SupplierCollectionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/supplier-collections/SupplierCollectionsPage'),
);
const RebateAgreementsPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/agreements/RebateAgreementsPage'),
);
const RebateAgreementDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/agreements/RebateAgreementDetailPage'),
);
const RebateAuditsPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/audits/RebateAuditsPage'),
);
const RebateAuditDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/audits/RebateAuditDetailPage'),
);
const SupplierReportedAmountsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/supplier-reported-amounts/SupplierReportedAmountsPage'),
);
const RebateCalculationAuditPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/calculation-audit/RebateCalculationAuditPage'),
);
const RebateTierProximityPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/tier-proximity/RebateTierProximityPage'),
  'RebateTierProximityPage',
);
const ConvertDataPageWorkflow = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/upload-data/ConvertDataPageWorkflow'),
);
const DataUploadRouter = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/upload-data/DataUploadRouter'),
);
const ManualRebateCSVUpload = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/upload-data/ManualRebateCSVUpload'),
);
const RebateCsvTransformJobsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/upload-data/RebateCsvTransformJobsPage'),
  'RebateCsvTransformJobsPage',
);
const RebateCsvTransformRunPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/upload-data/RebateCsvTransformRunPage'),
);
const DivisionsPage = lazyRouteComponent(
  () => import('./pages/authenticated/divisions/DivisionsPage'),
);
const DivisionDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/divisions/DivisionDetailPage'),
);
const PriceQuoteSheetDashboard = lazyRouteComponent(
  () =>
    import('./pages/authenticated/price-sheet-dashboard/PriceQuoteSheetDashboard'),
  'PriceQuoteSheetDashboard',
);
const PriceQuoteSheetInboxPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/price-sheet-inbox/PriceQuoteSheetInboxPage'),
  'PriceQuoteSheetInboxPage',
);
const PricingInboxPage = lazyRouteComponent(
  () => import('./pages/authenticated/pricing/PricingInboxPage'),
  'PricingInboxPage',
);
const PricingNewRequestRoute = lazyRouteComponent(
  () => import('./pages/authenticated/pricing/PricingNewRequestRoute'),
  'PricingNewRequestRoute',
);
const PricingRequestDetailRoute = lazyRouteComponent(
  () => import('./pages/authenticated/pricing/PricingRequestDetailRoute'),
  'PricingRequestDetailRoute',
);
const PricingSpaRecordsPage = lazyRouteComponent(
  () => import('./pages/authenticated/pricing/PricingSpaRecordsPage'),
  'PricingSpaRecordsPage',
);
const PricingSpaRecordDetailRoute = lazyRouteComponent(
  () => import('./pages/authenticated/pricing/PricingSpaRecordDetailRoute'),
  'PricingSpaRecordDetailRoute',
);
const WarehousesPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/inventory-management/warehouses/WarehousesPage'),
  'WarehousesPage',
);
const ItemCatalogPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/ItemCatalogPage'),
);
const PricelistsPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/PricelistsPage'),
  'PricelistsPage',
);
const PricelistDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/PricelistDetailPage'),
  'PricelistDetailPage',
);
const ReviewsPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/ReviewsPage'),
  'ReviewsPage',
);
const ChangeSetDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/ChangeSetDetailPage'),
  'ChangeSetDetailPage',
);
const UtilitiesPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/utilities/UtilitiesPage'),
  'UtilitiesPage',
);
const CsvToXlsxPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/utilities/CsvToXlsxPage'),
  'CsvToXlsxPage',
);
const CompareCsvsPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/utilities/CompareCsvsPage'),
  'CompareCsvsPage',
);

const WarehouseDetailRoute = lazyRouteComponent(
  () =>
    import('./pages/authenticated/inventory-management/warehouses/$warehouseId'),
  'WarehouseDetailRoute',
);
const VendorPurchaseOrdersPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/replenishment/ReplenishmentPage'),
  'VendorPurchaseOrdersPage',
);
const SupplierDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/replenishment/suppliers/SupplierDetailPage'),
  'SupplierDetailPage',
);
const QueryBuilderPrintPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/query-builder/QueryBuilderPrintPage'),
  'QueryBuilderPrintPage',
);
const InventoryPage = lazyRouteComponent(
  () => import('./pages/authenticated/procurement/inventory/InventoryPage'),
  'InventoryPage',
);
const ProductDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/product-detail/ProductDetailPage'),
  'ProductDetailPage',
);
const SupplierProductsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/supplier-products/SupplierProductsPage'),
  'SupplierProductsPage',
);
const ProcurementPurchaseOrdersPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/purchase-orders/ProcurementPurchaseOrdersPage'),
  'ProcurementPurchaseOrdersPage',
);
const ReplenishmentOrdersPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/replenishment/ReplenishmentOrdersPage'),
  'ReplenishmentOrdersPage',
);
import { Route as ProcurementPurchaseOrderDetailRoute } from './pages/authenticated/procurement/purchase-orders/$purchaseOrderId';
import { purchaseOrderLinesSearchSchema } from './pages/authenticated/procurement/purchase-orders/purchaseOrderFilters';
const WorkflowTemplatesPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowTemplatesPage'),
  'WorkflowTemplatesPage',
);
const ScheduledRerunsInboxPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/ScheduledRerunsInboxPage'),
  'ScheduledRerunsInboxPage',
);
const WorkflowsPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowsPage'),
  'WorkflowsPage',
);
const WorkflowStepTesterPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/workflows/step-tester/WorkflowStepTesterPage'),
  'WorkflowStepTesterPage',
);
const WorkflowTrafficGeneratorPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/workflows/traffic-generator/WorkflowTrafficGeneratorPage'),
  'WorkflowTrafficGeneratorPage',
);
const WorkflowDefinitionsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionsTab'),
  'WorkflowDefinitionsTab',
);
const WorkflowDefinitionLayoutPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionLayoutPage'),
  'WorkflowDefinitionLayoutPage',
);
const WorkflowDefinitionOverviewTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionOverviewTab'),
  'WorkflowDefinitionOverviewTab',
);
const WorkflowDefinitionStepsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionStepsTab'),
  'WorkflowDefinitionStepsTab',
);

const WorkflowDefinitionVersionsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionVersionsTab'),
  'WorkflowDefinitionVersionsTab',
);
const WorkflowDefinitionRunsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionRunsTab'),
  'WorkflowDefinitionRunsTab',
);
const WorkflowDefinitionTestTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionTestTab'),
  'WorkflowDefinitionTestTab',
);
const WorkflowAnalyticsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowAnalyticsTab'),
  'WorkflowAnalyticsTab',
);
const WorkflowApiTokensTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowApiTokensTab'),
  'WorkflowApiTokensTab',
);
const WorkflowFormSubmissionsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowFormSubmissionsTab'),
  'WorkflowFormSubmissionsTab',
);
const WorkflowManagerAgentTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowManagerAgentTab'),
  'WorkflowManagerAgentTab',
);
const WorkflowRunDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowRunDetailPage'),
  'WorkflowRunDetailPage',
);

const BackgroundAgentsPage = lazyRouteComponent(
  () => import('./pages/authenticated/agents/BackgroundAgentsPage'),
  'BackgroundAgentsPage',
);
const BackgroundAgentsListingPage = lazyRouteComponent(
  () => import('./pages/authenticated/agents/BackgroundAgentsListingPage'),
  'BackgroundAgentsListingPage',
);
const BackgroundAgentTemplatesPage = lazyRouteComponent(
  () => import('./pages/authenticated/agents/BackgroundAgentTemplatesPage'),
  'BackgroundAgentTemplatesPage',
);
const AgentDefinitionLayoutPage = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionLayoutPage'),
  'AgentDefinitionLayoutPage',
);
const AgentDefinitionAgentTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionAgentTab'),
  'AgentDefinitionAgentTab',
);
const AgentDefinitionToolsTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionToolsTab'),
  'AgentDefinitionToolsTab',
);
const AgentDefinitionTaskManagementTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionTaskManagementTab'),
  'AgentDefinitionTaskManagementTab',
);
const AgentDefinitionProceduresTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionProceduresTab'),
  'AgentDefinitionProceduresTab',
);
const AgentDefinitionResourcesTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionResourcesTab'),
  'AgentDefinitionResourcesTab',
);
const AgentDefinitionPermissionsTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionPermissionsTab'),
  'AgentDefinitionPermissionsTab',
);
const AgentDefinitionSessionsTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionSessionsTab'),
  'AgentDefinitionSessionsTab',
);
const AgentDefinitionSessionsLayout = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionSessionsLayout'),
  'AgentDefinitionSessionsLayout',
);
const AgentRunDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentRunDetailPage'),
  'AgentRunDetailPage',
);

const AgentMonitorPage = lazyRouteComponent(
  () => import('./pages/authenticated/agent-monitor/AgentMonitorPage'),
  'AgentMonitorPage',
);
const OperatorInterviewPage = lazyRouteComponent(
  () => import('./pages/authenticated/operator/OperatorInterviewPage'),
  'OperatorInterviewPage',
);
const MockUiStudioPage = lazyRouteComponent(
  () => import('./pages/authenticated/operator/MockUiStudioPage'),
  'MockUiStudioPage',
);
const UnifiedStudioPage = lazyRouteComponent(
  () => import('./pages/authenticated/operator/UnifiedStudioPage'),
  'UnifiedStudioPage',
);
const OperatorSopsPage = lazyRouteComponent(
  () => import('./pages/authenticated/operator/OperatorSopsPage'),
  'OperatorSopsPage',
);
const OperatorSopDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/operator/OperatorSopDetailPage'),
  'OperatorSopDetailPage',
);
const DataAssetsPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/data-assets/DataAssetsPage'),
  'DataAssetsPage',
);
const DataMappingsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/workflows/data-mappings/DataMappingsPage'),
  'DataMappingsPage',
);
const DataMappingDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/workflows/data-mappings/DataMappingDetailPage'),
  'DataMappingDetailPage',
);
const StatefulTablesPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/workflows/stateful-tables/StatefulTablesPage'),
  'StatefulTablesPage',
);
const StatefulTableDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/workflows/stateful-tables/StatefulTableDetailPage'),
  'StatefulTableDetailPage',
);

const WorkflowGroupsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowGroupsTab'),
  'WorkflowGroupsTab',
);
const WorkflowGroupEditorPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowGroupEditorPage'),
  'WorkflowGroupEditorPage',
);
// Feature-preview (pitch) pages live as file-based route modules under
// src/pages/authenticated/preview/ (see the tanstack-route-file-path rule).
import { Route as agentsPreviewRoute } from './pages/authenticated/preview/agents';
import { Route as tasksPreviewRoute } from './pages/authenticated/preview/tasks';
import { Route as workflowsPreviewRoute } from './pages/authenticated/preview/workflows';
import {
  ReceiptIcon,
  ClipboardTextIcon,
  TagIcon,
  ArrowCounterClockwiseIcon,
  TruckIcon,
  PresentationChartIcon,
  BuildingIcon,
  BuildingsIcon,
  TableIcon,
  CurrencyDollarIcon,
  FilePlusIcon,
  FileTextIcon,
  ShoppingCartIcon,
  BankIcon,
  TrendUpIcon,
  PackageIcon,
  TargetIcon,
  ChartBarIcon,
  DownloadIcon,
  ClockCounterClockwiseIcon,
  EnvelopeSimpleIcon,
  GearIcon,
  UsersIcon,
  WrenchIcon,
} from '@phosphor-icons/react';
import { slugify } from 'src/utils/strings';
import { Action, Subject } from '@erp/authz';

import { Route as rootRoute, type RouterContext } from 'src/pages/__root';
import { canManageStaffSectionsInRoute } from '@/hooks/authz/useCanManageStaffSections';
import { createRoute, redirect, Outlet } from '@tanstack/react-router';
const StatusPage = lazyRouteComponent(
  () => import('@/pages/(unauthenticated)/status/StatusPage'),
);
const PublicFormPage = lazyRouteComponent(
  () => import('@/pages/(unauthenticated)/forms/PublicFormPage'),
  'PublicFormPage',
);
const TeamsTabPage = lazyRouteComponent(
  () => import('@/pages/(unauthenticated)/teams-tab/TeamsTabPage'),
);
import { Route as loginRoute } from 'src/pages/(unauthenticated)/login';
import { Route as forgotPasswordRoute } from 'src/pages/(unauthenticated)/forgot-password';
import { Route as resetPasswordRoute } from 'src/pages/(unauthenticated)/reset-password';
import { Route as workosCallbackRoute } from 'src/pages/(unauthenticated)/workos-callback';
import { Route as googleCallbackRoute } from 'src/pages/(unauthenticated)/google-callback';
import { Route as mcpAuthorizeRoute } from 'src/pages/oauth/mcp/authorize';
import { Route as indexRoute } from 'src/pages/index';
import { Route as shipAndDebitLabelingRoute } from './pages/authenticated/ship-and-debit/labeling';
import { Route as shipAndDebitLabeledRoute } from './pages/authenticated/ship-and-debit/labeling/labeled';
import {
  Route as pathlessAuthenticatedRoute,
  requireModules,
  requireAdmin,
  requireAdminOrPermission,
  requireFeatureFlag,
  requirePermission,
  requireAnyPermission,
  featureSignupFallbackRedirect,
} from 'src/pages/authenticated/route';
const SettingsOrganizationLayoutPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/SettingsOrganizationPage'),
  'SettingsOrganizationLayoutPage',
);
const SettingsOrganizationSectionPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/SettingsOrganizationSectionPage'),
  'SettingsOrganizationSectionPage',
);
import {
  DEFAULT_ORG_SECTION_ID,
  LEGACY_SECTION_SLUG_MAP,
  ORGANIZATION_SETTINGS_SECTIONS,
  OrganizationSettingsSectionId,
  isSectionRouteClosed,
  sectionPath,
} from './pages/authenticated/settings/organization/organizationSettingsSections';
import { sectionRouteNavOptions } from './pages/authenticated/settings/organization/organizationSettingsSectionNav';
import {
  ERP_KEYS,
  getErpByKey,
  type ErpKey,
} from './pages/authenticated/settings/organization/components/cards/erp/erpDefinitions';
const UserPreferencesPage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/UserPreferencesPage'),
  'UserPreferencesPage',
);
const TenantPage = lazyRouteComponent(
  () => import('./pages/authenticated/tenant/TenantPage'),
  'TenantPage',
);
const OutOfOfficePage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/OutOfOfficePage'),
  'OutOfOfficePage',
);

const SecurityPage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/SecurityPage'),
  'SecurityPage',
);

const NavigationSettingsPage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/NavigationSettingsPage'),
  'NavigationSettingsPage',
);
const NotificationSettingsPage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/NotificationSettingsPage'),
  'NotificationSettingsPage',
);
const EmailSignaturePage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/EmailSignaturePage'),
  'EmailSignaturePage',
);
const EnumAssociationsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/core-enums/EnumAssociationsPage'),
  'EnumAssociationsPage',
);
const EmailTemplateEditorPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/email-templates/EmailTemplateEditorPage'),
  'EmailTemplateEditorPage',
);
const EmailTemplateVersionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/email-templates/EmailTemplateVersionsPage'),
  'EmailTemplateVersionsPage',
);
const PdfTemplateEditorPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/pdf-templates/PdfTemplateEditorPage'),
  'PdfTemplateEditorPage',
);
const PdfTemplateVersionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/pdf-templates/PdfTemplateVersionsPage'),
  'PdfTemplateVersionsPage',
);
const TemplateBlockEditorPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/template-blocks/TemplateBlockEditorPage'),
  'TemplateBlockEditorPage',
);
const TemplateBlockVersionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/template-blocks/TemplateBlockVersionsPage'),
  'TemplateBlockVersionsPage',
);
const SchemaDefinitionDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/schema-definitions/SchemaDefinitionDetailPage'),
  'SchemaDefinitionDetailPage',
);
import { z } from 'zod';
import {
  CashReceiptsSortBySearchSchema,
  CashReceiptsStatusTabSchema,
  CashReceiptsThenSortSearchSchema,
} from './pages/authenticated/accounts-receivable/cash-receipts/cashReceiptsListQuery';
import {
  CashReceiptLifecycleStatusSchema,
  CashReceiptPaymentTypeFilterSchema,
  CashReceiptTaskAssigneeFilterSchema,
  CashReceiptTaskStatusFilterSchema,
  CollectionThreadAssigneeFilterSchema,
  CollectionThreadAttentionSchema,
  CollectionThreadResolutionSchema,
  CollectionThreadSortBySchema,
  CollectionThreadStatusSchema,
  CollectionThreadTypeSchema,
  PaymentProcessorStatusSchema,
  ArInvoiceSortBySchema,
  RemittanceSortBySchema,
  thenSortSchema,
} from '@erp/rpc';
const DebtorsReportPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/debitors/DebtorsReportPage'),
);
const RebateReportsIndexPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/RebateReportsIndexPage'),
);
const DivisionRebateSummaryPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/reports/DivisionRebateSummaryPage'),
);
const SupplierTagReportPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/SupplierTagReportPage'),
);
const DailyEarningsPYEPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/DailyEarningsPYEPage'),
);
const MonthlyTrendingPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/MonthlyTrendingPage'),
);
const SupplierPaymentTermsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/reports/SupplierPaymentTermsPage'),
);
const ProfitabilityReportPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/ProfitabilityReportPage'),
);
const SupplierYoYPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/SupplierYoYPage'),
);
const BudgetComparisonPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/BudgetComparisonPage'),
);
const RebateDataExportPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/exports/RebateDataExportPage'),
);
const RebatesSettingsPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/settings/RebatesSettingsPage'),
);
const VendorFollowupsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/vendor-followups/VendorFollowupsPage'),
);
import { ProductModule } from './hooks/organization-config/useOrganizationEnabledModules';
const QAPage = lazyRouteComponent(() => import('./pages/qa/QAPage'));
const AzureOauthFlowCallback = lazyRouteComponent(
  () =>
    import('./pages/authenticated/oauth-azure-callback/AzureOauthFlowCallback'),
);
import config from 'src/config';
import { buildPosthogHeaders } from 'src/api/posthogHeaders';

const InvoiceDemosPage = lazyRouteComponent(
  () => import('src/pages/accounts-payable/invoice-demos'),
  'InvoiceDemosPage',
);
const InvoicesPage = lazyRouteComponent(
  () => import('./pages/authenticated/accounts-payable/invoices/InvoicesPage'),
  'InvoicesPage',
);
const PaymentsPage = lazyRouteComponent(
  () => import('./pages/authenticated/accounts-payable/payments/PaymentsPage'),
);
import { Route as InvoiceDetailRoute } from './pages/authenticated/accounts-payable/invoices/$invoiceId';
const PurchaseOrdersPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-payable/purchase-orders/PurchaseOrdersPage'),
  'PurchaseOrdersPage',
);
import { Route as PurchaseOrderDetailRoute } from './pages/authenticated/accounts-payable/purchase-orders/$purchaseOrderId';
import { Route as PaymentDetailRoute } from './pages/authenticated/accounts-payable/payments/$paymentId';
const ARSummaryPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/summary/ARSummaryPage'),
);
const ARInvoicesPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/invoices/ARInvoicesPage'),
);
import { ARInvoiceDetailRoute } from './pages/authenticated/accounts-receivable/invoices/$arInvoiceId';
import {
  Route as arPaymentsRoute,
  ArPaymentsIndexRoute as arPaymentsIndexRoute,
  ArPaymentDetailRoute as arPaymentDetailRoute,
} from './pages/authenticated/accounts-receivable/payments/route';
const RemittancesPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/remittances/RemittancesPage'),
  'RemittancesPage',
);
import { RemittanceDetailRoute } from './pages/authenticated/accounts-receivable/remittances/$remittanceId';
const ARCashReceiptsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/cash-receipts/ARCashReceiptsPage'),
  'ARCashReceiptsPage',
);
const IngestedSalesOrdersPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/ingested-sales-orders/IngestedSalesOrdersPage'),
  'IngestedSalesOrdersPage',
);
import { Route as IngestedSalesOrderDetailRoute } from './pages/authenticated/accounts-receivable/ingested-sales-orders/$salesOrderId';
import { CashReceiptDetailRoute } from './pages/authenticated/accounts-receivable/cash-receipts/$receiptId';
const ARReportsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/reports/ARReportsPage'),
);
const ARReportsListPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/reports/ARReportsListPage'),
);
const ARSavedReportPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/reports/saved/$savedReportId'),
  'ARSavedReportPage',
);
const ARCollectionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/collections/ARCollectionsPage'),
  'ARCollectionsPage',
);
const ARCustomerProfilePage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/collections/ARCustomerProfilePage'),
  'ARCustomerProfilePage',
);
const CollectionThreadDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/collections/CollectionThreadDetailPage'),
  'CollectionThreadDetailPage',
);
import { SavedReportNavItems } from './components/sidenav/saved-report-nav-items';
const CompaniesPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/CompaniesPage'),
);
const ContactsPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/ContactsPage'),
);
const CompanyDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/CompanyDetailPage'),
);
const CompanyContactsTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'ContactsTabPage',
);
const CompanyAddressesTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'AddressesTabPage',
);
const CompanyAliasesTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'AliasesTabPage',
);
const CompanyModulesTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'ModulesTabPage',
);
const CompanySupplierInfoTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'SupplierInfoTabPage',
);
const CompanyWarehouseConstraintsTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'WarehouseConstraintsTabPage',
);
const CompanyCustomerInfoTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'CustomerInfoTabPage',
);
const CompanyMetadataTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'MetadataTabPage',
);
const CompanyRebateAgreementsTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'RebateAgreementsTabPage',
);
const CompanyRebateProductGroupsTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'RebateProductGroupsTabPage',
);
const SharedReportPage = lazyRouteComponent(
  () => import('./pages/authenticated/shared/SharedReportPage'),
);

// tanstack router routes
// Create individual routes

const tenantRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/tenant',
  component: TenantPage,
  loader: () => ({
    crumb: msg('Tenant', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    if (!context.auth.currentUser?.tenantUser?.isAdmin) {
      throw redirect({ to: '/' });
    }
  },
});

// TODO remove this demo route
const invoiceDemosRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-payable/invoice-demos',
  component: InvoiceDemosPage,
  loader: () => ({
    crumb: msg('Invoice Demos', { context: 'page breadcrumb' }),
  }),
});

const chatHistoryRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/chat/history',
  staticData: {
    app: {
      nav: {
        label: msg('Chat history', {
          context: 'sidebar nav label, Assistant section',
        }),
        icon: <ClockCounterClockwiseIcon className="h-4 w-4" />,
        group: 'Assistant',
        order: 1,
        groupOrder: 0,
        // The Chat page links to history directly, so it doesn't need its own
        // sidebar entry. Leaving it visible also rendered a second "Assistant"
        // header, since pinned Chat already titles the pinned group with its
        // own `group` value.
        defaultHidden: true,
      },
      access: {
        featureFlag: 'chat-agent-enabled',
      },
    },
  },
  component: ChatHistoryPage,
  // The open conversation and the search query live in the URL so a reload
  // keeps the user's place and a conversation can be linked to.
  validateSearch: z.object({
    threadId: z.string().uuid().optional(),
    q: z.string().optional(),
  }),
  loader: () => ({
    crumb: msg('Chat history', { context: 'page breadcrumb' }),
  }),
  beforeLoad: () => {
    requireFeatureFlag('chat-agent-enabled');
  },
});

const supplierDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/suppliers/$id',
  component: CompanyDetailPage,
  loader: () => ({
    crumb: msg('Supplier', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [
        ProductModule.REBATES,
        ProductModule.PROCUREMENT,
        ProductModule.AP_INVOICING,
      ],
    });
  },
});

const companiesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/companies',
  staticData: {
    app: {
      nav: {
        label: msg('Companies', {
          context: 'sidebar nav label, Organization section',
        }),
        icon: <BuildingsIcon className="h-4 w-4" weight="fill" />,
        group: 'Organization',
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES, ProductModule.AP_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Company }],
      },
    },
  },
  component: CompaniesPage,
  loader: () => ({
    crumb: msg('Companies', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES, ProductModule.PROCUREMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Company,
    });
  },
});

const companyContactsListRoute = createRoute({
  getParentRoute: () => companiesRoute,
  path: 'contacts',
  component: ContactsPage,
  loader: () => ({
    crumb: msg('Contacts', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES, ProductModule.PROCUREMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Company,
    });
  },
});

const companyDetailRoute = createRoute({
  getParentRoute: () => companiesRoute,
  path: '$id',
  component: CompanyDetailPage,
  loader: async ({ params, context }) => {
    // Fetch company data for breadcrumb using queryClient for caching
    const { queryClient } = context;
    const queryKey = [`core/companies/${params.id}`];

    try {
      // Use queryClient.fetchQuery to leverage caching
      const response = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchResponse = await fetch(
            `${config.apiBaseUrl}/core/companies/${params.id}`,
            { credentials: 'include', headers: buildPosthogHeaders() },
          );

          if (!fetchResponse.ok) {
            throw new Error('Failed to fetch company');
          }

          const data = await fetchResponse.json();
          return data.data;
        },
      });

      return {
        crumb:
          response?.company?.name ||
          msg('Company', { context: 'page breadcrumb' }),
      };
    } catch (error) {
      // Silently handle error - fallback to default
    }

    return {
      crumb: msg('Company', { context: 'page breadcrumb' }),
    };
  },
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [
        ProductModule.REBATES,
        ProductModule.PROCUREMENT,
        ProductModule.AP_INVOICING,
      ],
    });
  },
});

const rebateSupplierDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/suppliers/$id',
  component: () => <CompanyDetailPage module="rebates" />,
  loader: () => ({
    crumb: msg('Rebate Supplier', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
});

// Tab subroutes shared by companyDetailRoute, supplierDetailRoute, and
// rebateSupplierDetailRoute. Each parent renders CompanyDetailPage (layout
// with sidebar + Outlet); tab content comes from these child routes.
// Tab paths are spelled out explicitly so TypeScript can preserve each
// route's literal `path` and register it in the router's type tree.
type ContactsTo =
  | '/companies/$id/contacts'
  | '/suppliers/$id/contacts'
  | '/rebates/suppliers/$id/contacts';

const makeCompanyTabChildren = <P extends AnyRoute>(
  parent: P,
  contactsTo: ContactsTo,
) => {
  const redirectToContacts = (id: string) =>
    redirect({
      to: contactsTo,
      params: { id },
      replace: true,
    });

  const gate = async (
    context: RouterContext,
    id: string,
    perm: { action: Action; subject: Subject },
  ) => {
    if (!(await context.authzClient.can(perm.action, perm.subject))) {
      throw redirectToContacts(id);
    }
  };

  const indexRoute = createRoute({
    getParentRoute: () => parent,
    path: '/',
    beforeLoad: ({ params }) => {
      throw redirectToContacts((params as { id: string }).id);
    },
  });
  const contactsRoute = createRoute({
    getParentRoute: () => parent,
    path: 'contacts',
    component: CompanyContactsTabPage,
  });
  const addressesRoute = createRoute({
    getParentRoute: () => parent,
    path: 'addresses',
    component: CompanyAddressesTabPage,
  });
  const aliasesRoute = createRoute({
    getParentRoute: () => parent,
    path: 'aliases',
    component: CompanyAliasesTabPage,
  });
  const modulesRoute = createRoute({
    getParentRoute: () => parent,
    path: 'modules',
    component: CompanyModulesTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Update,
        subject: Subject.Company,
      }),
  });
  const supplierInfoRoute = createRoute({
    getParentRoute: () => parent,
    path: 'supplier-info',
    component: CompanySupplierInfoTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Read,
        subject: Subject.Supplier,
      }),
  });
  const warehouseConstraintsRoute = createRoute({
    getParentRoute: () => parent,
    path: 'warehouse-constraints',
    component: CompanyWarehouseConstraintsTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Read,
        subject: Subject.Inventory,
      }),
  });
  const customerInfoRoute = createRoute({
    getParentRoute: () => parent,
    path: 'customer-info',
    component: CompanyCustomerInfoTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Read,
        subject: Subject.Customer,
      }),
  });
  const metadataRoute = createRoute({
    getParentRoute: () => parent,
    path: 'metadata',
    component: CompanyMetadataTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Update,
        subject: Subject.Company,
      }),
  });
  const rebateAgreementsRoute = createRoute({
    getParentRoute: () => parent,
    path: 'rebate-agreements',
    component: CompanyRebateAgreementsTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Read,
        subject: Subject.RebateAgreement,
      }),
  });
  const rebateProductGroupsRoute = createRoute({
    getParentRoute: () => parent,
    path: 'rebate-product-groups',
    component: CompanyRebateProductGroupsTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Read,
        subject: Subject.RebateAgreement,
      }),
  });

  return [
    indexRoute,
    contactsRoute,
    addressesRoute,
    aliasesRoute,
    modulesRoute,
    supplierInfoRoute,
    warehouseConstraintsRoute,
    customerInfoRoute,
    metadataRoute,
    rebateAgreementsRoute,
    rebateProductGroupsRoute,
  ] as const;
};

const companyDetailTabRoutes = makeCompanyTabChildren(
  companyDetailRoute,
  '/companies/$id/contacts',
);
const supplierDetailTabRoutes = makeCompanyTabChildren(
  supplierDetailRoute,
  '/suppliers/$id/contacts',
);
const rebateSupplierDetailTabRoutes = makeCompanyTabChildren(
  rebateSupplierDetailRoute,
  '/rebates/suppliers/$id/contacts',
);

const famousEDIShipmentsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/famous-edi-shipments',
  staticData: {
    app: {
      nav: {
        label: msg('Famous EDI (Shipments)', {
          context: 'sidebar nav label, Sales section',
        }),
        icon: <TruckIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 55,
      },
      access: {
        modulesAllOf: [
          ProductModule.ORDERFUL_EDI,
          ProductModule.FAMOUS_INTEGRATION,
        ],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: FamousEDIShipmentsPage,
  loader: () => ({
    crumb: msg('Famous EDI Shipments', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({
    famousShipmentId: z.string().optional(),
    purchaseOrderNumber: z.string().optional(),
    customerId: z.string().uuid().optional(),
    edi856Status: z.enum(['not_sent', 'pending', 'sent', 'error']).optional(),
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      allOf: [ProductModule.ORDERFUL_EDI, ProductModule.FAMOUS_INTEGRATION],
    });
  },
});

const famousEDIShipmentDetailRoute = createRoute({
  getParentRoute: () => famousEDIShipmentsRoute,
  path: '/$shipmentId',
  component: FamousEDIShipmentDetailRoute,
});

const famousEDIOrderConfirmationRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/famous-edi-order-confirmation',
  staticData: {
    app: {
      nav: {
        label: msg('Famous EDI (Order Confirmation)', {
          context: 'sidebar nav label, Sales section',
        }),
        icon: <FileTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 56,
      },
      access: {
        modulesAllOf: [
          ProductModule.ORDERFUL_EDI,
          ProductModule.FAMOUS_INTEGRATION,
        ],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: FamousEDIOrderConfirmationPage,
  loader: () => ({
    crumb: msg('Famous EDI Order Confirmation', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({
    famousOrderId: z.string().optional(),
    customerId: z.string().uuid().optional(),
    edi855Status: z.enum(['not_sent', 'pending', 'sent', 'error']).optional(),
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      allOf: [ProductModule.ORDERFUL_EDI, ProductModule.FAMOUS_INTEGRATION],
    });
  },
});

const famousEDIOrderConfirmationDetailRoute = createRoute({
  getParentRoute: () => famousEDIOrderConfirmationRoute,
  path: '/$orderConfirmationId',
  component: OrderConfirmationDetailRoute,
});

// const rebatesSubmittedRoute = createRoute({
//   getParentRoute: () => pathlessAuthenticatedRoute,
//   path: '/rebates/submitted',
//   component: SubmittedRebatesPage,
// });

const rebatesForecastingRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/analytics',
  staticData: {
    app: {
      nav: {
        label: msg('Analytics', {
          context: 'sidebar nav label, Rebates section',
        }),
        icon: <TrendUpIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebatesAnalyticsPage,
  loader: () => ({
    crumb: msg('Analytics', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  validateSearch: z.object({
    cumulative: z.boolean().optional(),
    ruleTagIds: z.array(z.string()).optional(),
    ruleType: z.array(z.enum(['conditional', 'non-conditional'])).optional(),
    agreementTagIds: z.array(z.string()).optional(),
    supplierTagIds: z.array(z.string()).optional(),
    agreementIds: z.array(z.string()).optional(),
    supplierIds: z.array(z.string()).optional(),
    divisionIds: z.array(z.string()).optional(),
    year: z.number().optional(),
    visibleLines: z.array(z.string()).optional(),
    primaryLine: z.string().optional(),
  }),
});

const rebatesDebtorsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/debtors',
  staticData: {
    app: {
      nav: {
        label: msg('Debtors Report', {
          context: 'sidebar nav label, Rebates section',
        }),
        icon: <BankIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 40,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: DebtorsReportPage,
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  loader: () => ({
    crumb: msg('Debtors Report', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({
    years: z.array(z.number()).optional(),
    showClosed: z.boolean().optional(),
    dataSources: z
      .array(z.enum(['estimates', 'reported', 'predictions']))
      .optional(),
    throughMonth: z.number().int().min(1).max(12).optional(),
  }),
});

const rebatesTierProximityRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/tier-proximity',
  staticData: {
    app: {
      nav: {
        label: msg('Tier Proximity', {
          context: 'sidebar nav label, Rebates section',
        }),
        icon: <TargetIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 50,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebateTierProximityPage,
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  loader: () => ({
    crumb: msg('Tier Proximity', { context: 'page breadcrumb' }),
  }),
});

const rebatesAuditsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/audits',
  staticData: {
    app: {
      nav: {
        label: msg('Audits', {
          context: 'sidebar nav label, Rebates section',
        }),
        icon: <ClipboardTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 60,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebateAuditsPage,
  loader: () => ({
    crumb: msg('Audits', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  validateSearch: z.object({
    year: z.number().optional(),
  }),
});

const rebatesAuditDetailRoute = createRoute({
  getParentRoute: () => rebatesAuditsRoute,
  path: '$agreementId',
  staticData: {
    app: {
      performance: { pageId: 'rebate-audit-detail', readiness: 'manual' },
    },
  },
  component: RebateAuditDetailPage,
  loader: () => ({
    crumb: msg('Audit Detail', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({
    year: z.number().optional(),
  }),
});

// const rebatesReconciliationRoute = createRoute({
//   getParentRoute: () => pathlessAuthenticatedRoute,
//   path: '/rebates/reconciliation',
//   component: RebatesReconciliationPage,
// });

const rebatesAgreementsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/agreements',
  staticData: {
    app: {
      nav: {
        label: msg('Agreements', {
          context: 'sidebar nav label, Rebates section',
        }),
        icon: <FileTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebateAgreementsPage,
  loader: () => ({
    crumb: msg('Agreements', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  validateSearch: z.object({
    search: z.string().optional(),
    year: z.number().optional(),
    showClosed: z.boolean().optional(),
    filterDuplicates: z.boolean().optional(),
    filterFinalized: z.boolean().optional(),
    filterDraft: z.boolean().optional(),
    filterMultiyear: z.boolean().optional(),
    filterClosed: z.boolean().optional(),
    filterTags: z.array(z.string()).optional(),
  }),
});

// Layout route for agreement detail - handles shared loader and auth
const rebatesAgreementLayoutRoute = createRoute({
  getParentRoute: () => rebatesAgreementsRoute,
  path: '$id',
  beforeLoad: async ({ context, params }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });

    // Fetch agreement data and extend context for child routes
    const { queryClient } = context;

    try {
      const result = await queryClient.fetchQuery(
        orpc.rebates.agreements.getById.queryOptions({
          input: { rebateAgreementId: params.id },
        }),
      );

      return { agreement: result.data };
    } catch {
      return { agreement: null };
    }
  },
  loader: ({ context }) => {
    // Use agreement from beforeLoad context for breadcrumb
    const { agreement } = context as { agreement?: { name?: string } };
    return {
      crumb:
        agreement?.name ||
        msg('Agreement Detail', { context: 'page breadcrumb' }),
    };
  },
  validateSearch: z.object({
    version: z.string().optional(),
  }),
});

// Index route - matches /rebates/agreements/{id} (no slug)
// Redirects to canonical URL with slug for better URLs
const rebatesAgreementIndexRoute = createRoute({
  getParentRoute: () => rebatesAgreementLayoutRoute,
  path: '/',
  beforeLoad: ({ context, params, search }) => {
    // Get agreement from parent's beforeLoad context
    const { agreement } = context as { agreement?: { name?: string } };

    if (agreement?.name) {
      // Redirect to canonical URL with slug
      const slug = slugify(agreement.name);
      if (slug) {
        throw redirect({
          to: '/rebates/agreements/$id/$slug',
          params: { id: params.id, slug },
          search,
          replace: true,
        });
      }
    }
  },
  component: RebateAgreementDetailPage,
});

// Slug route - matches /rebates/agreements/{id}/{slug}
// Static paths like 'create-version' and 'audit' take priority over this dynamic route
const rebatesAgreementSlugRoute = createRoute({
  getParentRoute: () => rebatesAgreementLayoutRoute,
  path: '$slug',
  component: RebateAgreementDetailPage,
});

const RebateAgreementCreateVersionPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/agreements/RebateAgreementCreateVersionPage'),
);
import { orpc } from './api/rpc/client';
import { CATALOG_METADATA_COLUMNS_STALE_TIME_MS } from './api/rpc/hooks/inventory-management/useItemCatalog';

const rebatesAgreementCreateVersionRoute = createRoute({
  getParentRoute: () => rebatesAgreementLayoutRoute,
  path: 'create-version',
  component: RebateAgreementCreateVersionPage,
  loader: () => ({
    crumb: msg('Create New Version', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebateCalculationAuditRoute = createRoute({
  getParentRoute: () => rebatesAgreementLayoutRoute,
  path: 'audit',
  component: RebateCalculationAuditPage,
  loader: () => ({
    crumb: msg('Calculation Audit', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
  validateSearch: z.object({
    date: z.string().optional(),
    calculationType: z.enum(['estimate', 'prediction']).optional(),
  }),
});

const rebatesSupplierCollectionsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/supplier-collections/$supplierId',
  component: SupplierCollectionsPage,
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
});

const rebatesSupplierReportedAmountsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/supplier-reported-amounts/$supplierId',
  component: SupplierReportedAmountsPage,
  loader: () => ({
    crumb: msg('Supplier Reported Amounts', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
});

const rebatesUploadDataRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/upload-data',
  staticData: {
    app: {
      nav: {
        label: msg('Upload Data', {
          context: 'sidebar nav label, Rebates section',
        }),
        icon: <ArrowCounterClockwiseIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 10,
        groupOrder: 50,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Execute, subject: Subject.RebateDataUpload },
        ],
      },
    },
  },
  component: DataUploadRouter,
  loader: () => ({
    crumb: msg('Upload Data', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Execute,
      subject: Subject.RebateDataUpload,
    });
  },
});

const rebatesUploadDataAIRoute = createRoute({
  getParentRoute: () => rebatesUploadDataRoute,
  path: 'ai',
  component: ConvertDataPageWorkflow,
  validateSearch: z.object({
    sessionId: z.string().uuid().optional(),
  }),
  loader: () => ({
    crumb: msg('AI-Powered Transform', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Execute,
      subject: Subject.RebateDataUpload,
    });
  },
});

const rebatesUploadDataManualRoute = createRoute({
  getParentRoute: () => rebatesUploadDataRoute,
  path: 'manual',
  component: ManualRebateCSVUpload,
  loader: () => ({
    crumb: msg('Manual Upload', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Execute,
      subject: Subject.RebateDataUpload,
    });
  },
});

const rebatesUploadDataJobsRoute = createRoute({
  getParentRoute: () => rebatesUploadDataRoute,
  path: 'jobs',
  component: RebateCsvTransformJobsPage,
  loader: () => ({
    crumb: msg('Jobs', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Execute,
      subject: Subject.RebateDataUpload,
    });
  },
});

const rebatesUploadDataRunRoute = createRoute({
  getParentRoute: () => rebatesUploadDataRoute,
  path: 'run/$sessionId',
  component: RebateCsvTransformRunPage,
  loader: () => ({
    crumb: msg('Run Timeline', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Execute,
      subject: Subject.RebateDataUpload,
    });
  },
});

const rebatesReportsIndexRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/reports',
  staticData: {
    app: {
      nav: {
        label: msg('Reports', {
          context: 'sidebar nav label, Rebates section',
        }),
        icon: <ChartBarIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 70,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebateReportsIndexPage,
  loader: () => ({
    crumb: msg('Reports', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
});

const rebatesDivisionSummaryRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'division-summary',
  component: DivisionRebateSummaryPage,
  loader: () => ({
    crumb: msg('Division Rebate Summary', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesSupplierTagSummaryRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'supplier-tag-summary',
  component: SupplierTagReportPage,
  loader: () => ({
    crumb: msg('Supplier Tag Summary', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesDailyEarningsPYERoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'daily-earnings-pye',
  component: DailyEarningsPYEPage,
  loader: () => ({
    crumb: msg('Daily Earnings PYE', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesMonthlyTrendingRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'monthly-trending',
  component: MonthlyTrendingPage,
  loader: () => ({
    crumb: msg('Monthly Trending', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesSupplierPaymentTermsRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'supplier-payment-terms',
  component: SupplierPaymentTermsPage,
  loader: () => ({
    crumb: msg('Supplier Payment Terms', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesProfitabilityReportRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'profitability',
  component: ProfitabilityReportPage,
  loader: () => ({
    crumb: msg('Profitability Report', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
  validateSearch: z.object({
    year: z.number().optional(),
    month: z.number().nullable().optional(),
    lineType: z.enum(['estimates', 'predictions', 'reported']).optional(),
    dimensions: z.string().optional(),
    sortBy: z.string().optional(),
    showDollar: z.boolean().optional(),
  }),
});

const rebatesSupplierYoYRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'supplier-yoy',
  component: SupplierYoYPage,
  loader: () => ({
    crumb: msg('Supplier YoY', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesBudgetComparisonRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'budget-comparison',
  component: BudgetComparisonPage,
  loader: () => ({
    crumb: msg('Budget vs. Forecast', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesDataExportRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/data-export',
  staticData: {
    app: {
      nav: {
        label: msg('Data Export', {
          context: 'sidebar nav label, Rebates section',
        }),
        icon: <DownloadIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 80,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebateDataExportPage,
  loader: () => ({
    crumb: msg('Data Export', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
});

const rebatesSettingsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/settings',
  staticData: {
    app: {
      nav: {
        label: msg('Settings', {
          context: 'sidebar nav label, Rebates section',
        }),
        icon: <GearIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 100,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateConfig },
          { action: Action.Update, subject: Subject.RebateConfig },
        ],
      },
    },
  },
  component: RebatesSettingsPage,
  loader: () => ({
    crumb: msg('Forecast & Followup Settings', {
      context: 'page breadcrumb',
    }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requireAnyPermission({
      context,
      permissions: [
        { action: Action.Read, subject: Subject.RebateConfig },
        { action: Action.Update, subject: Subject.RebateConfig },
      ],
    });
  },
});

const rebatesVendorFollowupsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/vendor-followups',
  staticData: {
    app: {
      nav: {
        label: msg('Vendor Followups', {
          context: 'sidebar nav label, Rebates section',
        }),
        icon: <EnvelopeSimpleIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 65,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: VendorFollowupsPage,
  loader: () => ({
    crumb: msg('Vendor Followups', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  validateSearch: z.object({
    year: z.coerce.number().optional(),
  }),
});

const priceQuoteSheetDashboardRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/price-sheet-dashboard',
  staticData: {
    app: {
      nav: {
        label: msg('Price Sheet Dashboard', {
          context: 'sidebar nav label, Procurement section',
        }),
        icon: <TableIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 10,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.ExternalPriceSheet },
        ],
      },
    },
  },
  component: PriceQuoteSheetDashboard,
  loader: () => ({
    crumb: msg('Price Sheet Dashboard', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ExternalPriceSheet,
      redirectTo: '/',
    });
  },
});

const priceQuoteSheetDashboardDetailRoute = createRoute({
  getParentRoute: () => priceQuoteSheetDashboardRoute,
  path: '/detail/$quoteSheetId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.ExternalPriceSheet },
        ],
      },
    },
  },
  component: PriceQuoteSheetDashboardDetailRoute,
});

const priceQuoteSheetInboxRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/price-sheet-inbox',
  staticData: {
    app: {
      nav: {
        label: msg('Price Sheets', {
          context: 'sidebar nav label, Procurement section',
        }),
        icon: <CurrencyDollarIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.ExternalPriceSheet },
        ],
      },
    },
  },
  component: PriceQuoteSheetInboxPage,
  loader: () => ({
    crumb: msg('Price Sheet Inbox', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ExternalPriceSheet,
      redirectTo: '/',
    });
  },
});

const priceQuoteSheetInboxDetailRoute = createRoute({
  getParentRoute: () => priceQuoteSheetInboxRoute,
  path: '/$quoteSheetId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.ExternalPriceSheet },
        ],
      },
    },
  },
  component: PriceQuoteSheetInboxDetailRoute,
});

// `pricingRoute` IS the SPA inbox — the inbox page renders an <Outlet /> so
// the new-request and detail child routes mount as side-drawer slideins on
// top of the inbox table (mirrors the AR/AP invoice slide-in pattern).
const pricingRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/pricing',
  staticData: {
    app: {
      nav: {
        label: msg('SPA Change Requests', {
          context: 'sidebar nav label, Pricing section',
        }),
        icon: <CurrencyDollarIcon className="h-4 w-4" weight="fill" />,
        group: 'Pricing',
        order: 10,
        groupOrder: 35,
      },
      access: {
        modulesAnyOf: [ProductModule.PRICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Pricing }],
      },
    },
  },
  component: PricingInboxPage,
  loader: () => ({
    crumb: msg('Pricing', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PRICING],
    });
  },
});

const pricingNewRequestRoute = createRoute({
  getParentRoute: () => pricingRoute,
  path: 'new',
  staticData: {
    app: {
      nav: {
        label: msg('New SPA Change Request', {
          context: 'sidebar nav label, Pricing section',
        }),
        icon: <FilePlusIcon className="h-4 w-4" weight="fill" />,
        group: 'Pricing',
        order: 20,
        groupOrder: 35,
      },
      access: {
        modulesAnyOf: [ProductModule.PRICING],
        permissionsAnyOf: [{ action: Action.Create, subject: Subject.Pricing }],
      },
    },
  },
  component: PricingNewRequestRoute,
  loader: () => ({
    crumb: msg('New SPA Request', { context: 'page breadcrumb' }),
  }),
});

const pricingRequestDetailRoute = createRoute({
  getParentRoute: () => pricingRoute,
  path: 'spas/$spaId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PRICING],
      },
    },
  },
  component: PricingRequestDetailRoute,
  loader: () => ({
    crumb: msg('SPA Request', { context: 'page breadcrumb' }),
  }),
});

const pricingSpaRecordsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/pricing/spa-records',
  staticData: {
    app: {
      nav: {
        label: msg('SPA Records', {
          context: 'sidebar nav label, Pricing section',
        }),
        icon: <CurrencyDollarIcon className="h-4 w-4" weight="fill" />,
        group: 'Pricing',
        order: 30,
        groupOrder: 35,
      },
      access: {
        modulesAnyOf: [ProductModule.PRICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Pricing }],
      },
    },
  },
  component: PricingSpaRecordsPage,
  loader: () => ({
    crumb: msg('SPA Records', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PRICING],
    });
  },
});

const pricingSpaRecordDetailRoute = createRoute({
  getParentRoute: () => pricingSpaRecordsRoute,
  path: '$spaId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PRICING],
      },
    },
  },
  component: PricingSpaRecordDetailRoute,
  loader: () => ({
    crumb: msg('SPA Record', { context: 'page breadcrumb' }),
  }),
});

// Layout route — provides the "Locations" breadcrumb and renders <Outlet />
// so the index (list) route or the detail route can take over the body.
const warehousesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/inventory-management/warehouses',
  staticData: {
    app: {
      nav: {
        label: msg('Locations', {
          context:
            'sidebar nav label, Inventory Management section: warehouses, distribution centres and branches',
        }),
        icon: <BuildingsIcon className="h-4 w-4" weight="fill" />,
        group: 'Inventory Management',
        order: 10,
        groupOrder: 25,
      },
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Warehouse }],
      },
    },
  },
  component: () => <Outlet />,
  loader: () => ({
    crumb: msg('Locations', {
      context: 'page breadcrumb: warehouses, distribution centres and branches',
    }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.INVENTORY_MANAGEMENT],
    });
  },
});

const warehousesIndexRoute = createRoute({
  getParentRoute: () => warehousesRoute,
  path: '/',
  component: WarehousesPage,
});

const warehouseDetailRoute = createRoute({
  getParentRoute: () => warehousesRoute,
  path: '$warehouseId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Warehouse }],
      },
    },
  },
  component: WarehouseDetailRoute,
});

const itemCatalogRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/item-master/catalog',
  staticData: {
    app: {
      nav: {
        label: msg('Item Catalog', {
          context: 'sidebar nav label, Item Master section',
        }),
        icon: <TableIcon className="h-4 w-4" weight="fill" />,
        group: 'Item Master',
        order: 10,
        groupOrder: 27,
      },
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
      performance: { pageId: 'item-catalog', readiness: 'manual' },
    },
  },
  component: ItemCatalogPage,
  // A navigation from the Lists page lands here scoped to that list.
  validateSearch: z.object({
    itemListId: z.string().uuid().optional(),
  }),
  loader: ({ context }) => {
    // Warm the advanced-filter column picker before the panel opens. Do not
    // await — navigation should not wait on this app-DB lookup.
    void context.queryClient
      .ensureQueryData({
        ...orpc.inventoryManagement.products.listMetadataColumns.queryOptions({
          input: {},
        }),
        staleTime: CATALOG_METADATA_COLUMNS_STALE_TIME_MS,
      })
      .catch(() => undefined);
    return {
      crumb: msg('Item Catalog', { context: 'page breadcrumb' }),
    };
  },
});

const reviewsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/item-master/reviews',
  staticData: {
    app: {
      nav: {
        label: msg('Reviews', {
          context: 'sidebar nav label, Item Master section',
        }),
        icon: <ClipboardTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Item Master',
        order: 20,
        groupOrder: 27,
        // Back to the review you were in after a trip to the catalog.
        resumeLastVisited: true,
      },
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: ReviewsPage,
  loader: () => ({
    crumb: msg('Reviews', { context: 'page breadcrumb' }),
  }),
});

const changeSetDetailRoute = createRoute({
  getParentRoute: () => reviewsRoute,
  path: '$changeSetId',
  staticData: {
    app: {
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: ChangeSetDetailPage,
  // `?mode=review` opens the review in Review mode.
  validateSearch: ReviewPageSearchSchema,
  loader: async ({ params, context }) => {
    // Fetch the change set name for the breadcrumb, cached via queryClient.
    try {
      const data = await context.queryClient.fetchQuery(
        orpc.inventoryManagement.metadataChangeSets.get.queryOptions({
          input: { id: params.changeSetId },
        }),
      );
      return {
        crumb:
          data?.changeSet?.name ??
          msg('Change set', { context: 'page breadcrumb' }),
      };
    } catch {
      return { crumb: msg('Change set', { context: 'page breadcrumb' }) };
    }
  },
});

const pricelistManagementRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/item-master/pricelist-management',
  staticData: {
    app: {
      nav: {
        label: msg('Pricelist Management', {
          context: 'sidebar nav label, Item Master section',
        }),
        icon: <TagIcon className="h-4 w-4" weight="fill" />,
        group: 'Item Master',
        order: 30,
        groupOrder: 27,
      },
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: PricelistsPage,
  // The active tab and the Price Changes table's filters/sort/page live in the
  // URL so a filtered view is shareable and survives a reload. The `pc*` keys
  // are the cases table's own (see pricelistCaseListState).
  validateSearch: z
    .object({
      tab: z.enum(['by-company', 'history', 'cases']).optional(),
    })
    .merge(PricelistCasesSearchSchema)
    .merge(PricelistHistorySearchSchema),
  loader: () => ({
    crumb: msg('Pricelist Management', { context: 'page breadcrumb' }),
  }),
});

const pricelistDetailRoute = createRoute({
  getParentRoute: () => pricelistManagementRoute,
  path: '$pdfDocumentId',
  staticData: {
    app: {
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: PricelistDetailPage,
  loader: async ({ params, context }) => {
    // Fetch the pricelist name for the breadcrumb, cached via queryClient.
    try {
      const data = await context.queryClient.fetchQuery(
        orpc.inventoryManagement.pricelist.get.queryOptions({
          input: { pdfDocumentId: params.pdfDocumentId },
        }),
      );
      return { crumb: data.name };
    } catch {
      return { crumb: msg('Pricelist', { context: 'page breadcrumb' }) };
    }
  },
});

const itemMasterUtilitiesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/item-master/utilities',
  staticData: {
    app: {
      nav: {
        label: msg('Utilities', {
          context: 'sidebar nav label, Item Master section',
        }),
        icon: <WrenchIcon className="h-4 w-4" weight="fill" />,
        group: 'Item Master',
        order: 40,
        groupOrder: 27,
      },
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: () => <Outlet />,
  loader: () => ({
    crumb: msg('Utilities', { context: 'page breadcrumb' }),
  }),
});

const itemMasterUtilitiesIndexRoute = createRoute({
  getParentRoute: () => itemMasterUtilitiesRoute,
  path: '/',
  component: UtilitiesPage,
});

const csvToXlsxRoute = createRoute({
  getParentRoute: () => itemMasterUtilitiesRoute,
  path: 'csv-to-xlsx',
  staticData: {
    app: {
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: CsvToXlsxPage,
  loader: () => ({
    crumb: msg('CSV to Excel (XLSX) converter', { context: 'page breadcrumb' }),
  }),
});

const compareCsvsRoute = createRoute({
  getParentRoute: () => itemMasterUtilitiesRoute,
  path: 'compare-csvs',
  staticData: {
    app: {
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: CompareCsvsPage,
  loader: () => ({
    crumb: msg('Compare Files', { context: 'page breadcrumb' }),
  }),
  // A pricelist's Compare action lands here with that pricelist already in the
  // first slot; the Utilities tile for pricelists opens straight on that tab.
  validateSearch: compareCsvsSearchSchema,
});

const replenishmentRoute = createRoute({
  // Under the procurement filter route, which owns `divisionIds` and
  // `warehouseIds` (shared with the forecast screens).
  getParentRoute: () => procurementFiltersRoute,
  path: '/procurement/replenishment',
  staticData: {
    app: {
      nav: {
        label: msg('Outgoing Purchase Orders', {
          context: 'sidebar nav label, Procurement section',
        }),
        icon: <TrendUpIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 30,
        groupOrder: 30,
        subItems: <OrderStatusNavItem type="purchase" />,
      },
      // Stable id: the route id changed when it moved under the filter route.
      performance: {
        pageId: 'procurement-replenishment',
        readiness: 'route-rendered',
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAllOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
          { action: Action.Create, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: VendorPurchaseOrdersPage,
  validateSearch: buyingBoardSearchSchema,
  loader: () => ({
    crumb: msg('Outgoing Purchase Orders', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const replenishmentSupplierDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/replenishment/suppliers/$supplierId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAllOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
          { action: Action.Create, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: SupplierDetailPage,
  // The page's own title (the supplier's name) replaces this crumb once the
  // supplier loads, so the trail reads Procurement > Supplier Details > name.
  loader: () => ({
    crumb: msg('Supplier', { context: 'page breadcrumb' }),
    crumbAncestors: [
      {
        title: msg('Supplier Details', { context: 'page breadcrumb' }),
        path: '/procurement/replenishment',
      },
    ],
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

// Deprecated: the Replenishment Orders browser was folded into the PO Status
// and TO Status pages (which now have Origin=Replenishment + Division filters
// and Created-by / Lines columns). Kept as a redirect so old bookmarks/links —
// PO tab → PO Status, TO tab → TO Status — keep working.
const replenishmentOrdersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/replenishment/orders',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAllOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: ReplenishmentOrdersPage,
  validateSearch: z.object({
    type: z.enum(['po', 'to']).optional(),
    search: z.string().optional(),
    status: z.array(z.string()).optional(),
    myPendingApprovals: z.boolean().optional(),
    divisionIds: z.array(z.string().uuid()).optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
  loader: () => ({
    crumb: msg('Replenishment Orders', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const queryBuilderPrintRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/query-builder/print',
  component: QueryBuilderPrintPage,
  loader: () => ({ crumb: msg('Report', { context: 'page breadcrumb' }) }),
  validateSearch: z.object({ k: z.string().optional() }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.PROCUREMENT] });
  },
});

const procurementPurchaseOrdersRoute = createRoute({
  // Under the procurement filter route, so the division filter it shares with
  // the forecast screens and the buyer board carries between them.
  getParentRoute: () => procurementFiltersRoute,
  path: '/procurement/purchase-orders',
  staticData: {
    app: {
      nav: {
        label: msg('Purchase Order Status', {
          context: 'sidebar nav label, Procurement section',
        }),
        icon: <ShoppingCartIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 25,
        groupOrder: 30,
        nestUnder: '/procurement/replenishment',
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: ProcurementPurchaseOrdersPage,
  loader: () => ({
    crumb: msg('Purchase Order Status', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
  validateSearch: purchaseOrderLinesSearchSchema,
});

const procurementPurchaseOrderDetailRoute = createRoute({
  getParentRoute: () => procurementPurchaseOrdersRoute,
  path: '/$purchaseOrderId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: ProcurementPurchaseOrderDetailRoute,
  loader: () => ({
    crumb: msg('Purchase Order', { context: 'page breadcrumb' }),
  }),
});

const inventoryRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/inventory-management/inventory',
  staticData: {
    app: {
      nav: {
        label: msg('Inventory', {
          context: 'sidebar nav label, Inventory Management section',
        }),
        icon: <PackageIcon className="h-4 w-4" weight="fill" />,
        group: 'Inventory Management',
        order: 20,
        groupOrder: 25,
      },
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: () => <Outlet />,
  loader: () => ({
    crumb: msg('Inventory', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.INVENTORY_MANAGEMENT],
    });
  },
});

const inventoryIndexRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: '/',
  component: InventoryPage,
});

const inventoryProductDetailRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: '$productId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ProductDetailPage,
  validateSearch: z.object({
    // 'overall' (default) = all-warehouse view; 'warehouse' = single-warehouse
    // focus view scoped to `warehouseId`.
    view: z.enum(['overall', 'warehouse']).optional(),
    warehouseId: z.string().optional(),
    // Overall view: comma-joined warehouse ids scoping every data section.
    // Empty/absent = org-wide.
    warehouseIds: z.string().optional(),
    // ProductV3 identity (the SKU). Carried here rather than in the path
    // because ~58k catalog keys contain a '/', which a path segment could only
    // express as %2F. See InventoryPage's row click.
    //
    // Coerced from number: the router parses search params with JSON.parse, so
    // an all-digit SKU like `?productV3Key=2677385` arrives as a NUMBER and a
    // bare z.string() throws SearchParamError straight to the router's raw
    // error screen. 191 real catalog keys are all digits. `true`/`false`/`null`
    // are the same hazard, hence coercing rather than widening to
    // string|number.
    productV3Key: z.coerce.string().optional(),
  }),
  loaderDeps: ({ search }) => ({ productV3Key: search.productV3Key }),
  loader: async ({ deps, context }) => {
    const { queryClient } = context;
    // The route match id is path + loaderDeps hash. Without the deps above,
    // every product shares one match and the breadcrumb goes stale on an
    // in-app hop between two products.
    if (!deps.productV3Key) {
      return { crumb: msg('Product', { context: 'page breadcrumb' }) };
    }
    try {
      const result = await queryClient.fetchQuery(
        orpc.procurement.inventorySnapshots.getByProduct.queryOptions({
          input: { productV3Key: deps.productV3Key },
        }),
      );
      return {
        crumb:
          result.productSku ??
          result.productName ??
          msg('Product', { context: 'page breadcrumb' }),
      };
    } catch {
      return { crumb: msg('Product', { context: 'page breadcrumb' }) };
    }
  },
});

const supplierProductsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/inventory-management/supplier-products',
  staticData: {
    app: {
      nav: {
        label: msg('Supplier Products', {
          context: 'sidebar nav label, Inventory Management section',
        }),
        icon: <PackageIcon className="h-4 w-4" weight="fill" />,
        group: 'Inventory Management',
        order: 25,
        groupOrder: 25,
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: () => <Outlet />,
  loader: () => ({
    crumb: msg('Supplier Products', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.INVENTORY_MANAGEMENT],
    });
  },
});

const supplierProductsIndexRoute = createRoute({
  getParentRoute: () => supplierProductsRoute,
  path: '/',
  component: SupplierProductsPage,
});

const supplierProductDetailRoute = createRoute({
  getParentRoute: () => supplierProductsRoute,
  path: '$productId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ProductDetailPage,
  validateSearch: z.object({
    // 'overall' (default) = all-warehouse view; 'warehouse' = single-warehouse
    // focus view scoped to `warehouseId`.
    view: z.enum(['overall', 'warehouse']).optional(),
    warehouseId: z.string().optional(),
    // Overall view: comma-joined warehouse ids scoping every data section.
    // Empty/absent = org-wide.
    warehouseIds: z.string().optional(),
    // ProductV3 identity (the SKU), carried in the search params for the same
    // reasons as inventoryProductDetailRoute above: '/' in ~58k keys, and
    // all-digit keys that JSON.parse would otherwise turn into numbers.
    productV3Key: z.coerce.string().optional(),
  }),
  loaderDeps: ({ search }) => ({ productV3Key: search.productV3Key }),
  loader: async ({ deps, context }) => {
    const { queryClient } = context;
    if (!deps.productV3Key) {
      return { crumb: msg('Product', { context: 'page breadcrumb' }) };
    }
    try {
      const result = await queryClient.fetchQuery(
        orpc.procurement.supplierProducts.getByProduct.queryOptions({
          input: { productV3Key: deps.productV3Key },
        }),
      );
      return {
        crumb:
          result.productSku ??
          result.productName ??
          msg('Product', { context: 'page breadcrumb' }),
      };
    } catch {
      return { crumb: msg('Product', { context: 'page breadcrumb' }) };
    }
  },
});

const apInvoicesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-payable/invoices',
  staticData: {
    app: {
      nav: {
        label: msg('Invoices', {
          context: 'sidebar nav label, Accounts Payable section',
        }),
        icon: <FileTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Payable',
        order: 10,
        groupOrder: 40,
      },
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.APInvoice }],
      },
    },
  },
  component: InvoicesPage,
  loader: () => ({
    crumb: msg('Invoices', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.AP_INVOICING],
    });
  },
  validateSearch: validateInvoicesSearch,
});

const apInvoiceDetailRoute = createRoute({
  getParentRoute: () => apInvoicesRoute,
  path: '/$invoiceId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.APInvoice }],
      },
    },
  },
  component: InvoiceDetailRoute,
});

const apPurchaseOrdersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-payable/purchase-orders',
  staticData: {
    app: {
      nav: {
        label: msg('Purchase Orders', {
          context: 'sidebar nav label, Accounts Payable section',
        }),
        icon: <ShoppingCartIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Payable',
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: PurchaseOrdersPage,
  loader: () => ({
    crumb: msg('Purchase Orders', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.AP_INVOICING],
    });
  },
  validateSearch: z.object({
    externalId: z.string().optional(),
    supplierId: z.string().optional(),
    status: z.array(z.string()).optional(),
    orderDateFrom: z.string().optional(),
    orderDateTo: z.string().optional(),
    deliveryDateFrom: z.string().optional(),
    deliveryDateTo: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const apPaymentsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-payable/payments',
  staticData: {
    app: {
      nav: {
        label: msg('Payments & Transactions', {
          context: 'sidebar nav label, Accounts Payable section',
        }),
        icon: <BankIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Payable',
        order: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.APInvoicePayment },
        ],
      },
    },
  },
  component: PaymentsPage,
  loader: () => ({
    crumb: msg('Payments & Transactions', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.AP_INVOICING],
    });
  },
  validateSearch: z.object({
    tab: z.enum(['payments', 'transactions']).optional(),
    status: z.string().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    approvalRequest: z.string().uuid().optional(),
    vendor: z.array(z.uuid()).optional(),
    q: z.string().optional(),
    processorStatus: z.array(PaymentProcessorStatusSchema).optional(),
    page: z.number().int().positive().optional(),
    pageSize: z.number().int().positive().optional(),
  }),
});

const apPurchaseOrderDetailRoute = createRoute({
  getParentRoute: () => apPurchaseOrdersRoute,
  path: '/$purchaseOrderId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: PurchaseOrderDetailRoute,
});

const apPaymentDetailRoute = createRoute({
  getParentRoute: () => apPaymentsRoute,
  path: '/$paymentId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.APInvoicePayment },
        ],
      },
    },
  },
  component: PaymentDetailRoute,
});

// Accounts Receivable routes
const arSummaryRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/summary',
  staticData: {
    app: {
      nav: {
        label: msg('Summary', {
          context: 'sidebar nav label, Accounts Receivable section',
        }),
        icon: <PresentationChartIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 10,
        groupOrder: 45,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARInvoice }],
      },
    },
  },
  component: ARSummaryPage,
  loader: () => ({ crumb: msg('Summary', { context: 'page breadcrumb' }) }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
});

const arIngestedSalesOrdersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/ingested-sales-orders',
  staticData: {
    app: {
      nav: {
        label: msg('Ingested Sales Orders', {
          context: 'sidebar nav label, Accounts Receivable section',
        }),
        icon: <ShoppingCartIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 15,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: IngestedSalesOrdersPage,
  loader: () => ({
    crumb: msg('Ingested Sales Orders', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requireAnyPermission({
      context,
      permissions: [{ action: Action.Read, subject: Subject.SalesOrder }],
    });
  },
  validateSearch: z.object({
    customerId: z.string().optional(),
    status: z.array(z.string()).optional(),
    orderDateFrom: z.string().optional(),
    orderDateTo: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const arIngestedSalesOrderDetailRoute = createRoute({
  getParentRoute: () => arIngestedSalesOrdersRoute,
  path: '/$salesOrderId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: IngestedSalesOrderDetailRoute,
});

const arRemittancesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/remittances',
  staticData: {
    app: {
      nav: {
        label: msg('Remittances', {
          context: 'sidebar nav label, Accounts Receivable section',
        }),
        icon: <ReceiptIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 25,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARCash }],
      },
    },
  },
  component: RemittancesPage,
  loader: () => ({ crumb: msg('Remittances', { context: 'page breadcrumb' }) }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARCash,
    });
  },
  validateSearch: z.object({
    reviewFilter: z.enum(['needs_review', 'complete', 'all']).optional(),
    channel: z
      .array(z.enum(['email', 'edi', 'portal', 'manual', 'bank_memo']))
      .optional(),
    receivedDateFrom: z.string().optional(),
    receivedDateTo: z.string().optional(),
    statedTotalAmountMin: z.number().optional(),
    statedTotalAmountMax: z.number().optional(),
    customerId: z.array(z.string()).optional(),
    search: z.string().optional(),
    sortBy: RemittanceSortBySchema.optional().catch(undefined),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    thenSort: thenSortSchema(RemittanceSortBySchema)
      .optional()
      .catch(undefined),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const arInvoicesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/invoices',
  staticData: {
    app: {
      nav: {
        label: msg('Invoices', {
          context: 'sidebar nav label, Accounts Receivable section',
        }),
        icon: <FileTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARInvoice }],
      },
      performance: { pageId: 'ar-invoices', readiness: 'route-rendered' },
    },
  },
  component: ARInvoicesPage,
  loader: () => ({ crumb: msg('Invoices', { context: 'page breadcrumb' }) }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
  validateSearch: z.object({
    customerId: z.array(z.string()).optional(),
    status: z.array(z.string()).optional(),
    invoiceDateFrom: z.string().optional(),
    invoiceDateTo: z.string().optional(),
    dueDateFrom: z.string().optional(),
    dueDateTo: z.string().optional(),
    firstSentAtFrom: z.string().optional(),
    firstSentAtTo: z.string().optional(),
    sentState: z.enum(['sent', 'unsent']).optional(),
    overdue: z.boolean().optional(),
    search: z.string().optional(),
    sortBy: ArInvoiceSortBySchema.optional().catch(undefined),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    thenSort: thenSortSchema(ArInvoiceSortBySchema).optional().catch(undefined),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const arReportsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/reports',
  staticData: {
    app: {
      nav: {
        label: msg('Reports', {
          context: 'sidebar nav label, Accounts Receivable section',
        }),
        icon: <ChartBarIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 40,
        subItems: <SavedReportNavItems />,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARInvoice }],
      },
    },
  },
  component: ARReportsListPage,
  loader: () => ({ crumb: msg('Reports', { context: 'page breadcrumb' }) }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
});

const arNewReportRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/reports/new',
  component: ARReportsPage,
  loader: () => ({ crumb: msg('New Report', { context: 'page breadcrumb' }) }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
});

const arSavedReportRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/reports/saved/$savedReportId',
  component: ARSavedReportPage,
  validateSearch: z.object({}),
  loader: () => ({
    crumb: msg('Saved Report', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.AR_INVOICING],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
});

const arCollectionsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/collections',
  staticData: {
    app: {
      nav: {
        label: msg('Collections', {
          context: 'sidebar nav label, Accounts Receivable section',
        }),
        icon: <UsersIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 35,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARInvoice }],
      },
    },
  },
  component: ARCollectionsPage,
  loader: () => ({ crumb: msg('Collections', { context: 'page breadcrumb' }) }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
  validateSearch: z.object({
    search: z.string().optional(),
    tab: z.enum(['customers', 'threads']).optional(),
    // Threads tab view: list (flat, default) or inbox (grouped-by-customer).
    view: z.enum(['inbox', 'list']).optional(),
    // Inbox selection (only meaningful when tab=threads & view=inbox).
    customerId: z.string().uuid().optional(),
    threadId: z.string().uuid().optional(),
    // Threads "list" view table state — server-side sort, filter, pagination.
    // Namespaced (thread*) so the filters don't collide with the inbox params.
    // `.catch` degrades a stale/unknown sort (e.g. a bookmarked `sortBy=urgency`
    // from before that sort was removed) to the default instead of erroring.
    sortBy: CollectionThreadSortBySchema.optional().catch(undefined),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    thenSort: thenSortSchema(CollectionThreadSortBySchema)
      .optional()
      .catch(undefined),
    page: z.number().int().min(1).optional(),
    pageSize: z.number().int().min(1).max(200).optional(),
    threadStatus: z.array(CollectionThreadStatusSchema).optional(),
    threadType: z.array(CollectionThreadTypeSchema).optional(),
    threadResolution: z.array(CollectionThreadResolutionSchema).optional(),
    // Waiting-on facet: org-defined option keys, so plain strings.
    threadWaitingOn: z.array(z.string()).optional(),
    // Attention lens (needs_me / waiting / all). `.catch` degrades a stale
    // bookmarked value to the default instead of erroring.
    threadAttention:
      CollectionThreadAttentionSchema.optional().catch(undefined),
    threadSearch: z.string().optional(),
    // "Assigned to" facet: user ids plus the 'unassigned' sentinel. `.catch`
    // discards stale/invalid URL values instead of erroring.
    threadAssignees: z
      .array(CollectionThreadAssigneeFilterSchema)
      .optional()
      .catch(undefined),
  }),
});

const arCustomerProfileRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/collections/$customerId',
  component: ARCustomerProfilePage,
  loader: () => ({
    crumb: msg('Customer Profile', { context: 'page breadcrumb' }),
    crumbAncestors: [
      {
        title: msg('Collections', { context: 'page breadcrumb' }),
        path: '/accounts-receivable/collections',
      },
    ],
  }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
});

const arCollectionThreadDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/collections/$customerId/threads/$threadId',
  component: CollectionThreadDetailPage,
  loader: () => ({ crumb: msg('Thread', { context: 'page breadcrumb' }) }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
  // `from` marks which ranked list the user arrived from (AR summary card or
  // the Collections threads tab), enabling an origin-aware "Back" link and
  // Previous/Next walking of that list.
  validateSearch: z.object({
    from: z.enum(['summary', 'threads']).optional(),
  }),
});

const arInvoiceDetailRoute = createRoute({
  getParentRoute: () => arInvoicesRoute,
  path: '/$arInvoiceId',
  component: ARInvoiceDetailRoute,
});

const arRemittanceDetailRoute = createRoute({
  getParentRoute: () => arRemittancesRoute,
  path: '/$remittanceId',
  component: RemittanceDetailRoute,
});

const arCashReceiptsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/cash-receipts',
  staticData: {
    app: {
      nav: {
        label: msg('Cash Receipts', {
          context: 'sidebar nav label, Accounts Receivable section',
        }),
        icon: <ReceiptIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 15,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARCash }],
      },
      performance: { pageId: 'ar-cash-receipts', readiness: 'route-rendered' },
    },
  },
  component: ARCashReceiptsPage,
  loader: () => ({
    crumb: msg('Cash Receipts', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
  },
  validateSearch: z.object({
    statusTab: CashReceiptsStatusTabSchema.optional(),
    paymentDateFrom: z.string().optional(),
    paymentDateTo: z.string().optional(),
    customerId: z.array(z.string()).optional(),
    paymentType: z.array(CashReceiptPaymentTypeFilterSchema).optional(),
    paymentAmountMin: z.number().optional(),
    paymentAmountMax: z.number().optional(),
    search: z.string().optional(),
    lifecycleStatus: z.array(CashReceiptLifecycleStatusSchema).optional(),
    taskStatus: z.array(CashReceiptTaskStatusFilterSchema).optional(),
    taskAssignee: z.array(CashReceiptTaskAssigneeFilterSchema).optional(),
    sortBy: CashReceiptsSortBySearchSchema.optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    thenSort: CashReceiptsThenSortSearchSchema.optional().catch(undefined),
    page: z.number().optional(),
    pageSize: z.number().optional(),
    /** The receipt shown in the right-hand detail pane (collapsed layout). */
    receiptId: z.string().optional(),
  }),
});

/**
 * The receipt detail used to be its own child route rendering a slide-over.
 * It now lives inline on the list page, keyed by the `receiptId` search param,
 * so the old path only survives to keep existing links and bookmarks working.
 */
const arCashReceiptDetailRoute = createRoute({
  getParentRoute: () => arCashReceiptsRoute,
  path: '/$receiptId',
  component: CashReceiptDetailRoute,
});

const divisionsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/divisions',
  staticData: {
    app: {
      nav: {
        label: msg('Divisions', {
          context: 'sidebar nav label, Organization section',
        }),
        icon: <BuildingIcon className="h-4 w-4" weight="fill" />,
        group: 'Organization',
        order: 10,
        groupOrder: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Division }],
      },
    },
  },
  component: DivisionsPage,
  loader: () => ({
    crumb: msg('Divisions', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Division,
    });
  },
});

const divisionRoute = createRoute({
  getParentRoute: () => divisionsRoute,
  path: '$divisionId',
  component: DivisionDetailPage,
  loader: async ({ params, context }) => {
    // Fetch division data for breadcrumb using queryClient for caching
    const { queryClient } = context;
    const queryKey = [`organization/divisions/${params.divisionId}`];

    try {
      // Use queryClient.fetchQuery to leverage caching
      const response = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchResponse = await fetch(
            `${config.apiBaseUrl}/organization/divisions/${params.divisionId}`,
            { credentials: 'include', headers: buildPosthogHeaders() },
          );

          if (!fetchResponse.ok) {
            throw new Error('Failed to fetch division');
          }

          const data = await fetchResponse.json();
          return data.data;
        },
      });

      return {
        crumb:
          response?.division?.name ||
          msg('Division Details', { context: 'page breadcrumb' }),
      };
    } catch (error) {
      // Silently handle error - fallback to default
    }

    return {
      crumb: msg('Division Details', { context: 'page breadcrumb' }),
    };
  },
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

/**
 * Inbound Emails: every email the org's ingest mailboxes received. Opens to
 * Gently staff or holders of `Read InboundEmail` (the server enforces the
 * same grant on the list endpoint); the entry lives in the user dropdown, not
 * the sidebar, gated the same way in `nav-user.tsx`.
 *
 * Still declared here rather than co-located: its Email Rules child is
 * Gently-staff debug tooling gated by `requireAdmin`, which `no-is-admin`
 * freezes outside allowlisted files, and `tanstack-route-file-path` cannot
 * resolve a child whose parent lives in another module. Both move together
 * once Email Rules gets a permission subject.
 */
const inboundEmailsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/inbound-emails',
  staticData: {
    app: {
      access: {
        adminOrPermission: {
          action: Action.Read,
          subject: Subject.InboundEmail,
        },
      },
    },
  },
  component: InboundEmailsPage,
  loader: () => ({
    crumb: msg('Inbound Emails', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    await requireAdminOrPermission({
      context,
      action: Action.Read,
      subject: Subject.InboundEmail,
    });
  },
  validateSearch: z.object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    inboundEmailId: z.string().optional(),
    search: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    cc: z.string().optional(),
    subject: z.string().optional(),
    body: z.string().optional(),
    upn: z.string().optional(),
    sortBy: z.enum(INBOUND_EMAIL_SORT_COLUMN_IDS).optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
  }),
});

const inboundEmailRulesRoute = createRoute({
  getParentRoute: () => inboundEmailsRoute,
  path: '/email-rules',
  component: InboundEmailRulesPage,
  loader: () => ({
    crumb: msg('Email Rules', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context, redirectTo: '/inbound-emails' });
  },
});

// The Tasks queue page lives as a co-located route module under
// src/pages/authenticated/tasks/ (with its /tasks/$taskId detail sibling);
// both are re-exported near the top of this file and added to the tree below.
// The former /tasks → /inbox?tab=tasks redirect is gone — /tasks is now the
// task queue itself. Inbox deep links (/inbox?tab=tasks&taskId=…) still work.

const documentRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/documents/$documentId',
  staticData: {
    app: {
      access: {},
    },
  },
  component: DocumentDetailPage,
});

const settingsOrganizationSearchSchema = z.object({
  section: z.string().optional(),
});

const settingsOrganizationLayoutRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/organization',
  component: SettingsOrganizationLayoutPage,
  validateSearch: settingsOrganizationSearchSchema,
  loader: () => ({
    crumb: msg('Organization Settings', { context: 'page breadcrumb' }),
  }),
});

const KNOWN_SECTION_IDS = new Set<OrganizationSettingsSectionId>(
  ORGANIZATION_SETTINGS_SECTIONS.map((s) => s.id),
);

const resolveSectionId = (
  requested: string | undefined,
): OrganizationSettingsSectionId => {
  if (!requested) return DEFAULT_ORG_SECTION_ID;
  if (LEGACY_SECTION_SLUG_MAP[requested])
    return LEGACY_SECTION_SLUG_MAP[requested];
  if (KNOWN_SECTION_IDS.has(requested as OrganizationSettingsSectionId))
    return requested as OrganizationSettingsSectionId;
  return DEFAULT_ORG_SECTION_ID;
};

const settingsOrganizationIndexRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: '/',
  validateSearch: settingsOrganizationSearchSchema,
  beforeLoad: ({ search }) => {
    throw redirect({ to: sectionPath(resolveSectionId(search.section)) });
  },
});

const settingsOrganizationSectionRoutes = ORGANIZATION_SETTINGS_SECTIONS
  // `erp` and `external-apis` are nested layout routes (list + detail
  // sub-pages) defined below, so they're excluded from the flat
  // section-route generation.
  .filter((section) => section.id !== 'erp' && section.id !== 'external-apis')
  .map((section) =>
    createRoute({
      getParentRoute: () => settingsOrganizationLayoutRoute,
      path: section.id,
      // Sections that declare `sidebarNav` also appear in the main sidebar,
      // gated exactly as the section is.
      ...sectionRouteNavOptions(section),
      component: () => (
        <SettingsOrganizationSectionPage sectionId={section.id} />
      ),
      loader: () => ({ crumb: section.title }),
      // fallow-ignore-next-line complexity
      beforeLoad: async ({ context }) => {
        const isGentlyStaff = !!context.auth.currentUser?.isAdmin;
        if (section.featureFlag || section.hiddenWhenFeatureFlag) {
          await waitForFeatureFlags();
        }
        // Staff-only sections also open for Update OrganizationSettings
        // holders; other requiredPermission bypasses stay isAdmin-only.
        const canOpenStaffSection =
          isGentlyStaff ||
          (!!section.gentlyOnly &&
            (await canManageStaffSectionsInRoute(context)));
        const closed = isSectionRouteClosed(section, {
          isGentlyStaff: canOpenStaffSection,
          isModuleEnabled: context.isModuleEnabled,
          isFeatureFlagEnabled: (flag) =>
            isBooleanFeatureFlagEnabled(flag, false),
        });
        const requiredAnyOf = isGentlyStaff
          ? []
          : [
              ...(section.requiredPermission
                ? [section.requiredPermission]
                : []),
              ...(section.requiredAnyPermission ?? []),
            ];
        const allowedByPermission =
          requiredAnyOf.length === 0 ||
          (
            await Promise.all(
              requiredAnyOf.map(({ action, subject }) =>
                context.authzClient.can(action, subject),
              ),
            )
          ).some(Boolean);
        if (closed || !allowedByPermission) {
          throw redirect({ to: sectionPath(DEFAULT_ORG_SECTION_ID) });
        }
      },
    }),
  );

// ERP integrations: a nested layout ("ERP" list + per-ERP detail sub-pages) so
// the breadcrumb reads "Organization Settings › ERP › <name>".
const settingsOrganizationErpLayoutRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: 'erp',
  component: Outlet,
  loader: () => ({
    crumb: msg('ERP Submission API', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    if (!context.isModuleEnabled(ProductModule.ORDER_MANAGEMENT)) {
      throw redirect({ to: sectionPath(DEFAULT_ORG_SECTION_ID) });
    }
  },
});

const settingsOrganizationErpIndexRoute = createRoute({
  getParentRoute: () => settingsOrganizationErpLayoutRoute,
  path: '/',
  component: () => <SettingsOrganizationSectionPage sectionId="erp" />,
});

const settingsOrganizationErpDetailRoute = createRoute({
  getParentRoute: () => settingsOrganizationErpLayoutRoute,
  path: '$erpKey',
  beforeLoad: ({ params }) => {
    if (!ERP_KEYS.includes(params.erpKey as ErpKey)) {
      throw redirect({ to: sectionPath('erp') });
    }
  },
  loader: ({ params }) => ({
    crumb:
      getErpByKey(params.erpKey)?.name ??
      msg('ERP', { context: 'page breadcrumb' }),
  }),
  component: () => {
    const { erpKey } = settingsOrganizationErpDetailRoute.useParams();
    return (
      <SettingsOrganizationSectionPage
        sectionId="erp"
        erpDetailKey={erpKey as ErpKey}
      />
    );
  },
});

const ExternalApiDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/external-apis/ExternalApiDetailPage'),
  'ExternalApiDetailPage',
);

// External APIs: a nested layout (registry list + per-API detail) so the
// breadcrumb reads "Organization Settings › External APIs › API Detail".
const settingsOrganizationExternalApisLayoutRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: 'external-apis',
  component: Outlet,
  loader: () => ({
    crumb: msg('External APIs', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    const isGentlyStaff = !!context.auth.currentUser?.isAdmin;
    if (!isGentlyStaff) {
      const allowed = await context.authzClient.can(
        Action.Read,
        Subject.Integration,
      );
      if (!allowed) {
        throw redirect({ to: sectionPath(DEFAULT_ORG_SECTION_ID) });
      }
    }
  },
});

const settingsOrganizationExternalApisIndexRoute = createRoute({
  getParentRoute: () => settingsOrganizationExternalApisLayoutRoute,
  path: '/',
  component: () => (
    <SettingsOrganizationSectionPage sectionId="external-apis" />
  ),
});

const settingsOrganizationExternalApiDetailRoute = createRoute({
  getParentRoute: () => settingsOrganizationExternalApisLayoutRoute,
  path: '$apiId',
  component: ExternalApiDetailPage,
  loader: () => ({ crumb: msg('API Detail', { context: 'page breadcrumb' }) }),
});

const settingsOrganizationEmailTemplateLayoutRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: 'email-templates/$templateId',
  component: Outlet,
  loader: () => ({
    crumb: msg('Email Template Editor', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

const settingsOrganizationEmailTemplateEditorRoute = createRoute({
  getParentRoute: () => settingsOrganizationEmailTemplateLayoutRoute,
  path: '/',
  component: EmailTemplateEditorPage,
  validateSearch: z.object({}),
});

const settingsOrganizationEmailTemplateVersionRoute = createRoute({
  getParentRoute: () => settingsOrganizationEmailTemplateLayoutRoute,
  path: '/v/$versionNumber',
  component: lazyRouteComponent(
    () =>
      import('./pages/authenticated/settings/organization/components/cards/email-templates/EmailTemplateVersionView'),
    'EmailTemplateVersionView',
  ),
  loader: ({ params }) => ({ crumb: `v${params.versionNumber}` }),
  validateSearch: z.object({}),
});

const settingsOrganizationEmailTemplateVersionsRoute = createRoute({
  getParentRoute: () => settingsOrganizationEmailTemplateLayoutRoute,
  path: '/versions',
  component: EmailTemplateVersionsPage,
  loader: () => ({ crumb: msg('Versions', { context: 'page breadcrumb' }) }),
  validateSearch: z.object({}),
});

const settingsOrganizationPdfTemplateLayoutRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: 'pdf-templates/$templateId',
  component: Outlet,
  loader: () => ({
    crumb: msg('PDF Template Editor', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

const settingsOrganizationPdfTemplateEditorRoute = createRoute({
  getParentRoute: () => settingsOrganizationPdfTemplateLayoutRoute,
  path: '/',
  component: PdfTemplateEditorPage,
  validateSearch: z.object({}),
});

const settingsOrganizationPdfTemplateVersionRoute = createRoute({
  getParentRoute: () => settingsOrganizationPdfTemplateLayoutRoute,
  path: '/v/$versionNumber',
  component: lazyRouteComponent(
    () =>
      import('./pages/authenticated/settings/organization/components/cards/pdf-templates/PdfTemplateVersionView'),
    'PdfTemplateVersionView',
  ),
  loader: ({ params }) => ({ crumb: `v${params.versionNumber}` }),
  validateSearch: z.object({}),
});

const settingsOrganizationPdfTemplateVersionsRoute = createRoute({
  getParentRoute: () => settingsOrganizationPdfTemplateLayoutRoute,
  path: '/versions',
  component: PdfTemplateVersionsPage,
  loader: () => ({ crumb: msg('Versions', { context: 'page breadcrumb' }) }),
  validateSearch: z.object({}),
});

const settingsOrganizationTemplateBlockLayoutRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: 'template-blocks/$templateBlockId',
  component: Outlet,
  loader: () => ({
    crumb: msg('Template Block Editor', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

const settingsOrganizationTemplateBlockEditorRoute = createRoute({
  getParentRoute: () => settingsOrganizationTemplateBlockLayoutRoute,
  path: '/',
  component: TemplateBlockEditorPage,
  validateSearch: z.object({}),
});

const settingsOrganizationTemplateBlockVersionRoute = createRoute({
  getParentRoute: () => settingsOrganizationTemplateBlockLayoutRoute,
  path: '/v/$versionNumber',
  component: lazyRouteComponent(
    () =>
      import('./pages/authenticated/settings/organization/components/cards/template-blocks/TemplateBlockVersionView'),
    'TemplateBlockVersionView',
  ),
  loader: ({ params }) => ({ crumb: `v${params.versionNumber}` }),
  validateSearch: z.object({}),
});

const settingsOrganizationTemplateBlockVersionsRoute = createRoute({
  getParentRoute: () => settingsOrganizationTemplateBlockLayoutRoute,
  path: '/versions',
  component: TemplateBlockVersionsPage,
  loader: () => ({ crumb: msg('Versions', { context: 'page breadcrumb' }) }),
  validateSearch: z.object({}),
});

const settingsOrganizationSchemaDefinitionDetailRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: 'schema-definitions/$schemaId',
  component: SchemaDefinitionDetailPage,
  loader: () => ({
    crumb: msg('Schema Definition', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

// Legacy redirect: /settings/organization/<old-slug> → new path. Keeps
// bookmarks working after slug renames. Specific section routes above win
// over this dynamic match.
const settingsOrganizationLegacyRedirectRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: '$section',
  beforeLoad: ({ params }) => {
    throw redirect({ to: sectionPath(resolveSectionId(params.section)) });
  },
});

export const settingsUserLayoutRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/user',
  component: UserPreferencesPage,
  loader: () => ({
    crumb: msg('User Settings', { context: 'page breadcrumb' }),
  }),
});

attachSettingsUserLayout(settingsUserLayoutRoute);

const settingsUserIndexRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/settings/user/security' });
  },
});

const settingsUserSecurityRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: 'security',
  component: SecurityPage,
  loader: () => ({
    crumb: msg('Security', { context: 'page breadcrumb' }),
  }),
});

const settingsUserOutOfOfficeRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: 'out-of-office',
  component: OutOfOfficePage,
  loader: () => ({
    crumb: msg('Out of office', { context: 'page breadcrumb' }),
  }),
});

const settingsUserNavigationRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: 'navigation',
  component: NavigationSettingsPage,
  loader: () => ({
    crumb: msg('Navigation', { context: 'page breadcrumb' }),
  }),
});

const settingsUserNotificationsRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: 'notifications',
  component: NotificationSettingsPage,
  loader: () => ({
    crumb: msg('Notifications', { context: 'page breadcrumb' }),
  }),
});

const settingsUserEmailSignatureRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: 'email-signature',
  component: EmailSignaturePage,
  loader: () => ({
    crumb: msg('Email signature', { context: 'page breadcrumb' }),
  }),
});

const enumAssociationsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/organization/enums/$enumId/associations',
  component: EnumAssociationsPage,
  loader: () => ({
    crumb: msg('Enum Associations', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    if (!context.auth.currentUser?.isAdmin) {
      throw redirect({ to: '/settings/organization/enums' });
    }
  },
});

// Legacy standalone URLs — the pages moved under Organization Settings ›
// Integrations; redirects keep old bookmarks working.
const externalApisListRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/external-apis',
  beforeLoad: () => {
    throw redirect({ to: '/settings/organization/external-apis' });
  },
});

const externalApiDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/external-apis/$apiId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/settings/organization/external-apis/$apiId',
      params,
    });
  },
});

const oauthAzureFlowCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth-azure-callback',
  component: AzureOauthFlowCallback,
  validateSearch: z.object({
    code: z.string().optional(),
    error: z.string().optional(),
    error_description: z.string().optional(),
  }),
});

const qaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/qa',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: QAPage,
  // The selected flow lives in the URL so a reload keeps the user's place and
  // a flow can be linked to directly.
  validateSearch: z.object({
    flow: z.string().optional(),
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

const statusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/status',
  component: StatusPage,
});

// Hosted public workflow forms. Lives under the root (not
// pathlessAuthenticatedRoute) because forms can allow anonymous submissions;
// per-form auth/allowlists are enforced by the backend describe endpoint.
const publicFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forms/$slug',
  component: PublicFormPage,
});

// Static landing for the Gently Teams app's personal-scope tab. Referenced
// from erp-teams-app/manifest-template/manifest.json as
// `staticTabs[0].contentUrl = "{{BASE_URL}}/teams/tab"`. Lives under the
// root (not pathlessAuthenticatedRoute) so Teams can load it inside the
// iframe without bouncing through auth.
const teamsTabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/teams/tab',
  component: TeamsTabPage,
});

// Redirect old URL to new one
const shipAndDebitStaged844RedirectRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/staged-844',
  beforeLoad: ({ search }) => {
    throw redirect({
      to: '/ship-and-debit/staged',
      search: search as Record<string, unknown>,
    });
  },
});

const shipAndDebitSearch844LegacyRedirect = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/search-844',
  beforeLoad: () => {
    throw redirect({ to: '/ship-and-debit/search-rexel-files', search: true });
  },
});

export const backgroundAgentsLayoutRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/agents',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.BackgroundAgent },
        ],
      },
    },
  },
  component: BackgroundAgentsPage,
  loader: () => ({
    crumb: msg('Background Agents', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.BackgroundAgent,
      // Pitch page when signups are on; `/` (pre-teaser) when the kill switch
      // is set. See featureSignupFallbackRedirect.
      redirectTo: featureSignupFallbackRedirect('/preview/agents'),
    });
  },
});

// Child routes intentionally omit `staticData.app.access`.
// The parent `/agents` route enforces RBAC for the entire subtree.
const backgroundAgentsListingRoute = createRoute({
  getParentRoute: () => backgroundAgentsLayoutRoute,
  path: '/',
  component: BackgroundAgentsListingPage,
  loader: () => ({
    crumb: msg('Listing', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

const backgroundAgentTemplatesRoute = createRoute({
  getParentRoute: () => backgroundAgentsLayoutRoute,
  path: 'templates',
  component: BackgroundAgentTemplatesPage,
  loader: () => ({
    crumb: msg('Templates', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

export const agentDefinitionLayoutRoute = createRoute({
  getParentRoute: () => backgroundAgentsLayoutRoute,
  path: '/$agentDefinitionId',
  component: AgentDefinitionLayoutPage,
  loader: async ({ context, params }) => {
    const { queryClient } = context;

    const definition = await queryClient.ensureQueryData(
      orpc.agentDefinitions.get.queryOptions({
        input: {
          agentDefinitionId: params.agentDefinitionId,
        },
      }),
    );

    const crumb = definition?.name
      ? `Agent: ${definition?.name}`
      : 'Agent Definition';
    return {
      crumb,
    };
  },
  validateSearch: z.object({}),
});

const agentDefinitionAgentTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/',
  component: AgentDefinitionAgentTab,
  validateSearch: z.object({}),
});

const agentDefinitionToolsTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/tools',
  component: AgentDefinitionToolsTab,
  loader: () => ({
    crumb: msg('Tools', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

// Procedures tab — the operator's SOP "skills". The tab trigger additionally requires the agent
// to have a procedure-capable tool (web browser or remote desktop) — see hasProcedureCapableTool
// in agents/procedureConfig.ts.
const agentDefinitionProceduresTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/procedures',
  component: AgentDefinitionProceduresTab,
  loader: () => ({
    crumb: msg('Procedures', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

const agentDefinitionTaskManagementTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/task-management',
  component: AgentDefinitionTaskManagementTab,
  loader: () => ({
    crumb: msg('Task Management', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

const agentDefinitionResourcesTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/resources',
  component: AgentDefinitionResourcesTab,
  loader: () => ({
    crumb: msg('Resources', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

const agentDefinitionPermissionsTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/permissions',
  component: AgentDefinitionPermissionsTab,
  loader: () => ({
    crumb: msg('Permissions', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

// Evals tab — reuses the standalone Agent Evals surface. Admin-only, matching
// the global /agent-evals route; suites are not yet agent-scoped. A layout
// route (no component → renders an Outlet) so the run detail opens *within*
// the tab instead of navigating away to the standalone /agent-evals/$runId.
const agentDefinitionEvalsTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/evals',
  loader: () => ({
    crumb: msg('Evals', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
  validateSearch: z.object({}),
});

const agentDefinitionEvalsListRoute = createRoute({
  getParentRoute: () => agentDefinitionEvalsTabRoute,
  path: '/',
  component: AgentEvalsPage,
  validateSearch: z.object({}),
});

const agentDefinitionEvalRunDetailRoute = createRoute({
  getParentRoute: () => agentDefinitionEvalsTabRoute,
  path: '/$runId',
  component: AgentEvalRunDetailPage,
  loader: () => ({
    crumb: msg('Run', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

const agentDefinitionSessionsLayoutRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/sessions',
  component: AgentDefinitionSessionsLayout,
  loader: () => ({
    crumb: msg('Sessions', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

const agentDefinitionSessionsListRoute = createRoute({
  getParentRoute: () => agentDefinitionSessionsLayoutRoute,
  path: '/',
  component: AgentDefinitionSessionsTab,
  validateSearch: z.object({}),
});

const agentRunDetailRoute = createRoute({
  getParentRoute: () => agentDefinitionSessionsLayoutRoute,
  path: '/$agentRunId',
  component: AgentRunDetailPage,
  loader: () => ({
    crumb: msg('Run Detail', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

// 1) Layout parent: shows in matches, provides breadcrumb, renders WorkflowsPage with tabs
export const workflowsListLayoutRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/workflows',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowsPage,
  loader: () => ({
    crumb: msg('Workflows', { context: 'page breadcrumb' }),
    crumbDescription: msg('Manage and configure your workflows', {
      context: 'tooltip describing the workflows page',
    }),
  }),
  beforeLoad: async ({ context }) => {
    // Guards the whole /workflows subtree. Users without Workflow.Read land
    // on the pitch page when signups are on; with the kill switch set they
    // bounce to `/` like before the teaser pages shipped.
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Workflow,
      redirectTo: featureSignupFallbackRedirect('/preview/workflows'),
    });
  },
});

const workflowDefinitionsTabRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: '/',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  validateSearch: z.object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    mine: z.coerce.boolean().optional(),
    published: z.coerce.boolean().optional(),
    paused: z.coerce.boolean().optional(),
    scheduled: z.coerce.boolean().optional(),
    event: z.coerce.boolean().optional(),
    system: z.coerce.boolean().optional(),
    deleted: z.coerce.boolean().optional(),
    namespaces: z.array(z.string()).optional(),
    sortBy: z.enum(WORKFLOW_DEFINITION_SORT_FIELDS).optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
  }),
  component: WorkflowDefinitionsTab,
});

const workflowGroupsTabRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'groups',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowGroupsTab,
  loader: () => ({
    crumb: msg('Workflow Groups', { context: 'page breadcrumb' }),
  }),
});

const workflowGroupDetailRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'groups/$workflowGroupId',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowGroupEditorPage,
  loader: async ({ context, params }) => {
    if (params.workflowGroupId === 'new') {
      return {
        crumb: msg('New Workflow Group', { context: 'page breadcrumb' }),
      };
    }

    try {
      const group = await context.queryClient.ensureQueryData(
        orpc.workflows.getGroup.queryOptions({
          input: {
            workflowGroupId: params.workflowGroupId,
          },
        }),
      );

      return {
        crumb:
          group.name || msg('Workflow Group', { context: 'page breadcrumb' }),
      };
    } catch {
      return {
        crumb: msg('Workflow Group', { context: 'page breadcrumb' }),
      };
    }
  },
});

// Templates tab (renders at /workflows/templates)
const workflowTemplatesRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'templates',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowTemplatesPage,
  loader: () => ({
    crumb: msg('Templates', { context: 'page breadcrumb' }),
  }),
});

// Scheduled-reruns inbox (renders at /workflows/scheduled-reruns)
const workflowScheduledRerunsRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'scheduled-reruns',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: ScheduledRerunsInboxPage,
  loader: () => ({
    crumb: msg('Scheduled Reruns', { context: 'page breadcrumb' }),
    crumbDescription: msg(
      'Workflow runs scheduled to fire in the future, soonest first. Cancel one before it runs.',
      { context: 'tooltip describing the scheduled reruns page' },
    ),
  }),
});

// Step Tester tab (renders at /workflows/step-tester)
const workflowStepTesterRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'step-tester',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.WorkflowAdmin },
        ],
      },
    },
  },
  component: WorkflowStepTesterPage,
  loader: () => ({
    crumb: msg('Step Tester', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.WorkflowAdmin,
    });
  },
});

// Traffic Generator tab (renders at /workflows/traffic-generator)
const workflowTrafficGeneratorRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'traffic-generator',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.WorkflowAdmin },
        ],
      },
    },
  },
  component: WorkflowTrafficGeneratorPage,
  loader: () => ({
    crumb: msg('Traffic Generator', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.WorkflowAdmin,
    });
  },
});

const workflowDataAssetsRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'data-assets',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: DataAssetsPage,
  loader: () => ({
    crumb: msg('Data Assets', { context: 'page breadcrumb' }),
  }),
});

const workflowDataMappingsRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'data-mappings',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: DataMappingsPage,
  loader: () => ({
    crumb: msg('Data Mappings', { context: 'page breadcrumb' }),
  }),
});

const workflowDataMappingDetailRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'data-mappings/$dataMappingId',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: DataMappingDetailPage,
  loader: () => ({
    crumb: msg('Data Mapping', { context: 'page breadcrumb' }),
  }),
});

const workflowStatefulTablesRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'stateful-tables',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: StatefulTablesPage,
  loader: () => ({
    crumb: msg('Stateful Tables', { context: 'page breadcrumb' }),
  }),
});

const workflowStatefulTableDetailRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'stateful-tables/$tableId',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: StatefulTableDetailPage,
  loader: () => ({
    crumb: msg('Stateful Table', { context: 'page breadcrumb' }),
  }),
});

// ---------------------------------------------------------------------------
// Workflow viewer
//
// The URL identity is the Workflow (lineage), not a specific version. Without
// a version path segment the viewer shows the pinned definition; `/v/N`
// swaps to a specific version. Every inner tab reads the resolved `workflow`
// and `definition` off the matching layout route's loader data via
// `useWorkflowViewerContext()` — they never re-read the URL param.
// ---------------------------------------------------------------------------

/**
 * Shared resolver for both the pinned-view and version-view layout loaders.
 * Accepts either a concrete version number (for `/v/$n`) or null (pinned).
 * Returns loader data shared by both: the workflow, the resolved definition,
 * and the version number currently being viewed (or `null` when the workflow
 * has no pinned version yet).
 */
async function loadWorkflowViewerData({
  queryClient,
  workflowId,
  viewingVersion,
}: {
  queryClient: ReturnType<
    typeof import('@tanstack/react-query').useQueryClient
  >;
  workflowId: string;
  viewingVersion: number | null;
}) {
  const workflow = await queryClient.ensureQueryData(
    orpc.workflows.getWorkflow.queryOptions({
      input: { workflowId },
    }),
  );

  let targetDefinitionId: string | null = null;
  let resolvedVersion: number | null = null;

  if (viewingVersion !== null) {
    const versions = await queryClient.ensureQueryData(
      orpc.workflows.listVersions.queryOptions({
        input: { workflowId },
      }),
    );
    const match = versions.versions.find((v) => v.version === viewingVersion);
    if (match) {
      targetDefinitionId = match.id;
      resolvedVersion = match.version;
    }
  } else if (workflow.pinnedVersionId) {
    targetDefinitionId = workflow.pinnedVersionId;
  } else {
    // No pinned version — fall back to the latest version (e.g. an unpinned
    // draft created via "Save Draft" before the first "Create").
    const versions = await queryClient.ensureQueryData(
      orpc.workflows.listVersions.queryOptions({
        input: { workflowId },
      }),
    );
    const latest = versions.versions[0];
    if (latest) {
      targetDefinitionId = latest.id;
      resolvedVersion = latest.version;
    }
  }

  const definition = targetDefinitionId
    ? await queryClient.ensureQueryData(
        orpc.workflows.getDefinition.queryOptions({
          input: { workflowDefinitionId: targetDefinitionId },
        }),
      )
    : null;

  if (definition && resolvedVersion === null) {
    resolvedVersion = definition.version;
  }

  return { workflow, definition, viewingVersionNumber: resolvedVersion };
}

// Layout route for the pinned view (`/workflows/$workflowId`).
// Exported so the co-located memories route module can parent onto it while
// this layout is still centrally declared (see
// src/pages/authenticated/(workflow-detail)/memories/index.tsx). The module is
// imported below, so this is a deliberate lazy-resolved import cycle —
// `getParentRoute` thunks defer the binding access until the router builds.
export const workflowLayoutRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: '/$workflowId',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowDefinitionLayoutPage,
  loader: async ({ context, params }) => {
    // "new" is the create-workflow sentinel — the editor renders without
    // pre-loaded data in that case.
    if (params.workflowId === 'new') {
      return {
        crumb: msg('New Workflow', { context: 'page breadcrumb' }),
        workflow: null,
        definition: null,
        viewingVersionNumber: null,
      };
    }

    const data = await loadWorkflowViewerData({
      queryClient: context.queryClient,
      workflowId: params.workflowId,
      viewingVersion: null,
    });

    return {
      crumb: data.workflow?.name
        ? `Workflow: ${data.workflow.name}`
        : 'Workflow',
      ...data,
    };
  },
});

// Layout route for a specific version view (`/workflows/$workflowId/v/$n`).
// Nested under the pinned layout: chrome (header + tabs) is rendered by the
// outer layout, which reads whichever inner loader's data exists via
// `useWorkflowViewerContext`. This route is a transparent pass-through — its
// own component is just `<Outlet />` so tab children render in place without
// drawing the header a second time.
const workflowVersionLayoutRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/v/$versionNumber',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: Outlet,
  loader: async ({ context, params }) => {
    const parsed = Number.parseInt(params.versionNumber, 10);
    if (!Number.isFinite(parsed)) {
      return {
        crumb: msg('Version', { context: 'page breadcrumb' }),
        workflow: null,
        definition: null,
        viewingVersionNumber: null,
      };
    }

    const data = await loadWorkflowViewerData({
      queryClient: context.queryClient,
      workflowId: params.workflowId,
      viewingVersion: parsed,
    });

    return {
      crumb: `v${parsed}`,
      ...data,
    };
  },
});

// Tab routes — duplicated once for the pinned layout and once for the
// version layout so that both URL shapes expose the same tabbed UI. Each
// component reads `workflow` / `definition` / `viewingVersionNumber` from
// the nearest layout's loader data via `useWorkflowViewerContext()`.

const workflowDefinitionOverviewRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowDefinitionOverviewTab,
  validateSearch: z.object({
    edit: z.boolean().optional(),
    emailRuleId: z.string().uuid().optional(),
    triggerType: z
      .enum([
        'schedule_trigger',
        'event_trigger',
        'email_rule_trigger',
        'http_trigger',
        'form_trigger',
        'action_input',
        'none',
      ])
      .optional(),
  }),
});

const workflowVersionOverviewRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowDefinitionOverviewTab,
  validateSearch: z.object({
    edit: z.boolean().optional(),
    emailRuleId: z.string().uuid().optional(),
    /** Step to focus when opening the editor from overview (step id in graph) */
    stepId: z.string().optional(),
  }),
});

const workflowDefinitionStepsRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/steps',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowDefinitionStepsTab,
  loader: () => ({ crumb: msg('Steps', { context: 'page breadcrumb' }) }),
});

const workflowVersionStepsRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/steps',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowDefinitionStepsTab,
  loader: () => ({ crumb: msg('Steps', { context: 'page breadcrumb' }) }),
});

const workflowDefinitionRunsRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/runs',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowDefinitionRunsTab,
  loader: () => ({ crumb: msg('Runs', { context: 'page breadcrumb' }) }),
});

const workflowVersionRunsRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/runs',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowDefinitionRunsTab,
  loader: () => ({ crumb: msg('Runs', { context: 'page breadcrumb' }) }),
});

const workflowDefinitionVersionsRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/versions',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowDefinitionVersionsTab,
  loader: () => ({ crumb: msg('Versions', { context: 'page breadcrumb' }) }),
});

const workflowVersionVersionsRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/versions',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowDefinitionVersionsTab,
  loader: () => ({ crumb: msg('Versions', { context: 'page breadcrumb' }) }),
});

const workflowDefinitionTestRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/test',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowDefinitionTestTab,
  loader: () => ({ crumb: msg('Test', { context: 'page breadcrumb' }) }),
});

const workflowVersionTestRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/test',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowDefinitionTestTab,
  loader: () => ({ crumb: msg('Test', { context: 'page breadcrumb' }) }),
});

const workflowDefinitionAnalyticsRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/analytics',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowAnalyticsTab,
  loader: () => ({ crumb: msg('Analytics', { context: 'page breadcrumb' }) }),
});

const workflowVersionAnalyticsRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/analytics',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowAnalyticsTab,
  loader: () => ({ crumb: msg('Analytics', { context: 'page breadcrumb' }) }),
});

// HTTP API tab is lineage-scoped (trigger tokens bind to a Workflow lineage,
// not a definition version), so we only mount it on the pinned layout — there's no
// useful version-scoped variant. Links from the version layout fall through
// to the pinned URL via `paths.authenticated.workflows.workflow`.
// The tab is only linked in the UI when the viewed definition has an
// `http_trigger` step; the route remains reachable for deep links.
const workflowDefinitionApiTokensRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/tokens',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowApiTokensTab,
  loader: () => ({ crumb: msg('HTTP API', { context: 'page breadcrumb' }) }),
});

// Form submissions tab is lineage-scoped (submissions are workflow runs of
// the lineage). Only linked in the UI when the viewed definition has a
// `form_trigger` step.
const workflowDefinitionFormSubmissionsRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/submissions',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowFormSubmissionsTab,
  loader: () => ({
    crumb: msg('Form submissions', { context: 'page breadcrumb' }),
  }),
});

const workflowManagerAgentRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/manager',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowManagerAgentTab,
  loader: () => ({ crumb: msg('Manager', { context: 'page breadcrumb' }) }),
});

// Run detail is version-agnostic (runs belong to the workflow, their own id
// identifies the exact definition version that ran). Nested under the runs
// route so the runs table stays visible as a sidebar when viewing a run.
const workflowRunDetailRoute = createRoute({
  getParentRoute: () => workflowDefinitionRunsRoute,
  path: '/$workflowRunId',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workflow }],
      },
    },
  },
  component: WorkflowRunDetailPage,
  loader: async ({ context, params }) => {
    const { queryClient } = context;
    const run = await queryClient.ensureQueryData(
      orpc.workflows.getRun.queryOptions({
        input: {
          workflowRunId: params.workflowRunId,
        },
      }),
    );

    return {
      crumb: msg('Run {id}...', {
        context: 'page breadcrumb naming a workflow run by its truncated id',
        id: run.id.slice(0, 8),
      }),
    };
  },
});

// Shared report page (accessed via shareable links)
const sharedReportRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/shared/$token',
  component: SharedReportPage,
  loader: () => ({
    crumb: msg('Shared Report', { context: 'page breadcrumb' }),
  }),
});

// Agent Monitor — gated on Read:BackgroundAgent, the same permission the
// agent-definitions handlers it calls enforce server-side.

const agentMonitorDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/agent-monitor/$agentDefinitionId',
  component: AgentMonitorPage,
  loader: () => ({
    crumb: msg('Agent Detail', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.BackgroundAgent,
    });
  },
  validateSearch: z.object({}),
});

// Procedures (SOPs) live under Background Agents (`/agents/procedures`) — RBAC is
// inherited from `backgroundAgentsLayoutRoute`'s `Subject.BackgroundAgent` permission
// check, same convention as `agentDefinitionProceduresTabRoute`.
const backgroundAgentProceduresRoute = createRoute({
  getParentRoute: () => backgroundAgentsLayoutRoute,
  path: 'procedures',
  component: OperatorSopsPage,
  loader: () => ({
    crumb: msg('Procedures', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({
    review: z.boolean().optional(),
    taskId: z.uuid().optional(),
  }),
});

// Registered before the `$id` route below so the literal `new` segment isn't
// shadowed by the dynamic param matcher.
const backgroundAgentProcedureNewRoute = createRoute({
  getParentRoute: () => backgroundAgentsLayoutRoute,
  path: 'procedures/new',
  component: OperatorInterviewPage,
  loader: () => ({
    crumb: msg('Operator Procedure Generation', { context: 'page breadcrumb' }),
    crumbTo: '/agents/procedures',
  }),
  // agentId: launched from an Agent builder's Procedures tab — on finalize the
  // new SOP is auto-attached to that agent and we navigate back to its tab.
  // sessionId/agentRunId: persisted in the URL so a refresh/redeploy rehydrates the
  // running interview instead of orphaning the Kernel browser + losing the draft
  // (Fable audit #6).
  // startUrl/seedInstruction: the "Teach procedure" bridge from the Mock UI Studio (AI-340) —
  // deep-links here prefilled to interview against `${hostedUrl}?case=<id>`, so the taught SOP
  // inherits that startUrl and becomes a mock-backed procedure.
  validateSearch: z.object({
    agentId: z.string().optional(),
    sessionId: z.string().optional(),
    agentRunId: z.string().optional(),
    startUrl: z.string().optional(),
    seedInstruction: z.string().optional(),
  }),
});

// Legacy redirect: /agents/mock-ui → /evals/mocks/studio (AI-385). Preserves refresh-rehydrate search.
const backgroundAgentMockUiRoute = createRoute({
  getParentRoute: () => backgroundAgentsLayoutRoute,
  path: 'mock-ui',
  validateSearch: z.object({
    sessionId: z.string().optional(),
    agentRunId: z.string().optional(),
  }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/evals/mocks/studio', search });
  },
});

const backgroundAgentProcedureDetailRoute = createRoute({
  getParentRoute: () => backgroundAgentsLayoutRoute,
  path: 'procedures/$id',
  component: OperatorSopDetailPage,
  loader: () => ({
    crumb: msg('Procedure', { context: 'page breadcrumb' }),
    crumbTo: '/agents/procedures',
  }),
  validateSearch: z.object({ agentId: z.string().optional() }),
});

// Shared links management page

// Evals (AI-385) — the admin-only eval home, opened from the user menu. Relocated from the standalone
// /agent-evals (sidebar) + /agents/mock-ui (studio) into one tabbed surface: "Evals" (suites + runs,
// the AgentEvalsPage) and "Mock UIs" (the mock catalog → studio). The two legacy routes below are kept
// as redirects so old bookmarks/deep-links keep working.
const evalsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/evals',
  component: EvalsLayoutPage,
  loader: () => ({
    crumb: msg('Evals', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
  // `suiteId` deep-links to a specific suite (e.g. from Mock UI Studio's "Run eval" / the catalog).
  validateSearch: z.object({ suiteId: z.string().optional() }),
});

// Default tab: the eval suites + runs (the relocated AgentEvalsPage, standalone mode).
const evalsSuitesRoute = createRoute({
  getParentRoute: () => evalsRoute,
  path: '/',
  component: AgentEvalsPage,
});

const evalsRunDetailRoute = createRoute({
  getParentRoute: () => evalsRoute,
  path: 'runs/$runId',
  component: AgentEvalRunDetailPage,
  loader: () => ({
    crumb: msg('Run', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({}),
});

const evalsMocksRoute = createRoute({
  getParentRoute: () => evalsRoute,
  path: 'mocks',
  component: MockUiCatalogPage,
  loader: () => ({
    crumb: msg('Mock UIs', { context: 'page breadcrumb' }),
  }),
});

const evalsMockStudioRoute = createRoute({
  getParentRoute: () => evalsRoute,
  path: 'mocks/studio',
  component: MockUiStudioPage,
  loader: () => ({
    crumb: msg('Studio', { context: 'page breadcrumb' }),
  }),
  // sessionId/agentRunId persisted in the URL so a refresh rehydrates the running build; `site`
  // seeds a "modify existing" build when the catalog opens a specific mock; `sopId` seeds the
  // storyboard from a procedure's visual guide (AI-488 follow-on).
  validateSearch: z.object({
    sessionId: z.string().optional(),
    agentRunId: z.string().optional(),
    site: z.string().optional(),
    sopId: z.string().optional(),
  }),
});

// Unified live-capture Studio (AI-340 P4): one walkthrough of a real prod site → SOP + mock + eval.
// All session identity is persisted in the URL so a refresh/redeploy rehydrates the in-flight
// capture (sessionId), its finalized SOP (sopId), or the mock-build session (studioSessionId)
// instead of orphaning the Kernel browser / sandbox — same reasoning as the interview + studio.
const evalsUnifiedStudioRoute = createRoute({
  getParentRoute: () => evalsRoute,
  path: 'mocks/unified-studio',
  component: UnifiedStudioPage,
  loader: () => ({
    crumb: msg('Unified Studio', { context: 'page breadcrumb' }),
  }),
  validateSearch: z.object({
    sessionId: z.string().optional(),
    agentRunId: z.string().optional(),
    sopId: z.string().optional(),
    studioSessionId: z.string().optional(),
    studioRunId: z.string().optional(),
  }),
});

// Legacy redirect: /agent-evals → /evals (AI-385).
const agentEvalsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/agent-evals',
  validateSearch: z.object({ suiteId: z.string().optional() }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/evals', search });
  },
});

// Legacy redirect: /agent-evals/$runId → /evals/runs/$runId (AI-385).
const agentEvalRunDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/agent-evals/$runId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/evals/runs/$runId', params });
  },
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  workosCallbackRoute,
  googleCallbackRoute,
  mcpAuthorizeRoute,
  qaRoute,
  statusRoute,
  publicFormRoute,
  teamsTabRoute,
  oauthAzureFlowCallbackRoute,
  pathlessAuthenticatedRoute.addChildren([
    chatRoute,
    chatHistoryRoute,
    inboxRoute,
    tasksQueueRoute,
    workbooksRoute,
    workbookDetailRoute,
    workbookLibraryReportRoute,
    projectsRoute,
    projectDetailRoute,
    taskDetailRoute,
    namespacesRoute,
    namespaceDetailRoute,
    taskPageQueueRoute,
    agentPageRoute,
    customersRoute,
    salesOrdersRoute,
    salesOrderDetailRoute,
    salesOrdersV2Route,
    salesOrderV2Route,
    enhancedSalesOrderDraftPageRoute,
    salesOrdersV3RedirectRoute,
    salesOrderV3DetailRedirectRoute,
    evalsRoute.addChildren([
      evalsSuitesRoute,
      evalsRunDetailRoute,
      evalsMocksRoute,
      evalsMockStudioRoute,
      evalsUnifiedStudioRoute,
    ]),
    agentEvalsRoute,
    agentEvalRunDetailRoute,
    suppliersRoute,
    supplierDetailRoute.addChildren(supplierDetailTabRoutes),
    companiesRoute.addChildren([
      companyContactsListRoute,
      companyDetailRoute.addChildren(companyDetailTabRoutes),
    ]),
    invoiceDemosRoute, // TODO remove this demo route
    rebateSupplierDetailRoute.addChildren(rebateSupplierDetailTabRoutes),
    orderMetricsRoute,
    aiUsageRoute,
    workflowAdminRoute,
    userSessionsRoute,
    feedbackTriageRoute,
    salesReportsRoute,
    famousEDIRoute,
    famousEDIShipmentsRoute.addChildren([famousEDIShipmentDetailRoute]),
    famousEDIOrderConfirmationRoute.addChildren([
      famousEDIOrderConfirmationDetailRoute,
    ]),
    orderfulTransactionsRoute,
    ediTextMessagesRoute,
    // rebatesSubmittedRoute,
    rebatesForecastingRoute,
    rebatesDebtorsRoute,
    rebatesTierProximityRoute,
    rebatesAuditsRoute.addChildren([rebatesAuditDetailRoute]),
    rebatesReportsIndexRoute.addChildren([
      rebatesDivisionSummaryRoute,
      rebatesSupplierTagSummaryRoute,
      rebatesDailyEarningsPYERoute,
      rebatesMonthlyTrendingRoute,
      rebatesSupplierPaymentTermsRoute,
      rebatesProfitabilityReportRoute,
      rebatesSupplierYoYRoute,
      rebatesBudgetComparisonRoute,
    ]),
    rebatesDataExportRoute,
    rebatesSettingsRoute,
    rebatesVendorFollowupsRoute,
    // rebatesReconciliationRoute,
    rebatesAgreementsRoute.addChildren([
      rebatesAgreementLayoutRoute.addChildren([
        rebatesAgreementIndexRoute,
        rebatesAgreementSlugRoute,
        rebatesAgreementCreateVersionRoute,
        rebateCalculationAuditRoute,
      ]),
    ]),
    rebatesSupplierCollectionsRoute,
    rebatesSupplierReportedAmountsRoute,
    rebatesUploadDataRoute.addChildren([
      rebatesUploadDataAIRoute,
      rebatesUploadDataManualRoute,
      rebatesUploadDataJobsRoute,
      rebatesUploadDataRunRoute,
    ]),
    priceQuoteSheetDashboardRoute.addChildren([
      priceQuoteSheetDashboardDetailRoute,
    ]),
    priceQuoteSheetInboxRoute.addChildren([priceQuoteSheetInboxDetailRoute]),
    pricingRoute.addChildren([
      pricingNewRequestRoute,
      pricingRequestDetailRoute,
    ]),
    pricingSpaRecordsRoute.addChildren([pricingSpaRecordDetailRoute]),
    pricingMasterlistRoute,
    pricingResultsToUploadRoute,
    pricingVendorPortalsRoute,
    pricingSupplierPortalsRedirectRoute,
    warehousesRoute.addChildren([warehousesIndexRoute, warehouseDetailRoute]),
    supplyNetworkRoute,
    itemCatalogRoute,
    itemListsRoute,
    dailyReportsRoute,
    eatonDspRoute,
    priceListBreakPivotRoute,
    reviewsRoute.addChildren([changeSetDetailRoute]),
    reviewThreadLinkRoute,
    validationsRoute.addChildren([validationRuleDetailRoute]),
    pricelistManagementRoute.addChildren([pricelistDetailRoute]),
    pricelistCaseDetailRoute,
    itemMasterUtilitiesRoute.addChildren([
      itemMasterUtilitiesIndexRoute,
      csvToXlsxRoute,
      compareCsvsRoute,
    ]),
    createTransferOrderRoute,
    createPurchaseOrderRoute,
    replenishmentSupplierDetailRoute,
    replenishmentOrdersRoute,
    salesOrderStatusRoute,
    transferOrderStatusRoute,
    as400RawDataRoute,
    queryBuilderRoute,
    queryBuilderPrintRoute,
    obsoleteProductsRoute,
    millableReviewRoute,
    overridesRoute,
    freightOptionsRoute,
    procurementFiltersRoute.addChildren([
      forecastSummaryRoute,
      forecastDetailsRoute,
      replenishmentDetailRoute,
      replenishmentRoute,
      internalTransfersRoute,
      procurementPurchaseOrdersRoute.addChildren([
        procurementPurchaseOrderDetailRoute,
      ]),
    ]),
    forecastRunHistoryRoute,
    demandForecastingRoute.addChildren([
      demandForecastingIndexRoute,
      demandForecastingGeneralRoute,
      demandForecastingOverridesRoute,
      demandForecastingLeadTimeRoute,
      demandForecastingSurplusRoute,
      demandForecastingOutlierRulesRoute,
      demandForecastingSalesHistoryTransfersRoute,
      demandForecastingRunsRoute,
    ]),
    forecastRunsLegacyRoute,
    inventoryRoute.addChildren([
      inventoryIndexRoute,
      inventoryProductDetailRoute,
    ]),
    supplierProductsRoute.addChildren([
      supplierProductsIndexRoute,
      supplierProductDetailRoute,
    ]),
    apCommandCenterRoute,
    apMetricsRoute,
    apBulkPaymentsRoute,
    apThroughputRedirectRoute,
    apExceptionRulesRoute,
    apExceptionRuleSetCreateRoute,
    apExceptionRuleSetDetailRoute,
    apExceptionRuleSetEditRoute,
    apInvoicesRoute.addChildren([apInvoiceDetailRoute]),
    apPurchaseOrdersRoute.addChildren([apPurchaseOrderDetailRoute]),
    apPaymentsRoute.addChildren([apPaymentDetailRoute]),
    arSummaryRoute,
    arCashMetricsRoute,
    arCashDashboardRoute,
    arIngestedSalesOrdersRoute.addChildren([arIngestedSalesOrderDetailRoute]),
    arCashReceiptsRoute.addChildren([arCashReceiptDetailRoute]),
    arTasksRoute,
    arPaymentsRoute.addChildren([arPaymentsIndexRoute, arPaymentDetailRoute]),
    arRemittancesRoute.addChildren([arRemittanceDetailRoute]),
    arInvoicesRoute.addChildren([arInvoiceDetailRoute]),
    arReportsRoute,
    arNewReportRoute,
    arSavedReportRoute,
    arCollectionsRoute,
    arCustomerProfileRoute,
    arCheckDepositsRoute,
    arCheckDepositDetailRoute,
    arCollectionThreadDetailRoute,
    divisionsRoute.addChildren([divisionRoute]),
    inboundEmailsRoute.addChildren([inboundEmailRulesRoute]),
    documentsRoute,
    documentRoute,
    agentsPreviewRoute,
    tasksPreviewRoute,
    workflowsPreviewRoute,
    taskTypeCreateRoute,
    taskTypeDetailRoute,
    settingsOrganizationLayoutRoute.addChildren([
      settingsOrganizationIndexRoute,
      settingsOrganizationErpLayoutRoute.addChildren([
        settingsOrganizationErpIndexRoute,
        settingsOrganizationErpDetailRoute,
      ]),
      settingsOrganizationExternalApisLayoutRoute.addChildren([
        settingsOrganizationExternalApisIndexRoute,
        settingsOrganizationExternalApiDetailRoute,
      ]),
      settingsOrganizationEmailTemplateLayoutRoute.addChildren([
        settingsOrganizationEmailTemplateEditorRoute,
        settingsOrganizationEmailTemplateVersionRoute,
        settingsOrganizationEmailTemplateVersionsRoute,
      ]),
      settingsOrganizationPdfTemplateLayoutRoute.addChildren([
        settingsOrganizationPdfTemplateEditorRoute,
        settingsOrganizationPdfTemplateVersionRoute,
        settingsOrganizationPdfTemplateVersionsRoute,
      ]),
      settingsOrganizationTemplateBlockLayoutRoute.addChildren([
        settingsOrganizationTemplateBlockEditorRoute,
        settingsOrganizationTemplateBlockVersionRoute,
        settingsOrganizationTemplateBlockVersionsRoute,
      ]),
      settingsOrganizationSchemaDefinitionDetailRoute,
      ...settingsOrganizationSectionRoutes,
      settingsOrganizationLegacyRedirectRoute,
    ]),
    controlPlaneLinkRoute,
    schemaLabelingRoute,
    settingsUserLayoutRoute.addChildren([
      settingsUserIndexRoute,
      settingsUserSecurityRoute,
      settingsUserOutOfOfficeRoute,
      settingsUserNavigationRoute,
      settingsUserNotificationsRoute,
      settingsUserLanguageRoute,
      settingsUserKeyboardShortcutsRoute,
      settingsUserEmailSignatureRoute,
    ]),
    welcomeRoute,
    homeRoute,
    tenantRoute,
    enumAssociationsRoute,
    externalApisListRoute,
    externalApiDetailRoute,
    featureFlagsRoute,
    historicalImportRoute,
    backfillsRoute,
    maintenanceRoute.addChildren([
      maintenanceIndexRoute,
      maintenanceBackfillsRoute,
      maintenanceIndexesRoute,
      maintenanceLargeTablesRoute,
      maintenanceWorkerScalingRoute,
      maintenanceReconciliationRoute,
      maintenanceSchemaDriftRedirectRoute,
    ]),
    rexelMonitoringRoute,
    shipAndDebitExecutiveSummaryRoute,
    shipAndDebitDashboardRoute,
    shipAndDebitStaged844RedirectRoute,
    shipAndDebitStaged844Route,
    shipAndDebitStagedInternalRoute,
    shipAndDebitSearchTransactionsRoute,
    shipAndDebitNoClaimRoute,
    shipAndDebitVDMsRoute,
    shipAndDebitVDMDetailRoute,
    shipAndDebit844And849Route,
    shipAndDebit849ResponsesRoute,
    shipAndDebit849ResponseDetailRoute,
    shipAndDebitSampleLibraryRoute,
    shipAndDebit849ReviewRoute,
    shipAndDebitLabelingRoute,
    shipAndDebitLabeledRoute,
    shipAndDebitConfigAuthoringRoute,
    shipAndDebitSearch844Route,
    shipAndDebitActivityLogRoute,
    shipAndDebitEdi997AcknowledgementsRoute,
    shipAndDebitCompanyLocationsRoute,
    shipAndDebitRexelFilesRoute,
    shipAndDebitAdminReportsRoute,
    shipAndDebitSuppliersRoute,
    shipAndDebitSupplierDetailRoute,
    shipAndDebit844ConfigurationRoute,
    shipAndDebit844ConfigurationSupplierRoute,
    shipAndDebitSearch844LegacyRedirect,
    memoryBankNotesRoute,
    guardrailPoliciesRoute,
    integrationsListRoute,
    integrationDetailRoute,
    auditReportsRoute,
    notificationsHistoryRoute,
    appsGalleryRoute,
    appsRoute,
    appRoute,
    reportAppRoute,
    backgroundAgentsLayoutRoute.addChildren([
      backgroundAgentsListingRoute,
      backgroundAgentTemplatesRoute,
      backgroundAgentProceduresRoute,
      backgroundAgentProcedureNewRoute,
      backgroundAgentProcedureImportReviewRoute,
      backgroundAgentMockUiRoute,
      backgroundAgentProcedureDetailRoute,
      procedureVersionDetailRoute,
      procedureVersionsRoute,
      agentDefinitionLayoutRoute.addChildren([
        agentDefinitionAgentTabRoute,
        agentDefinitionToolsTabRoute,
        agentDefinitionProceduresTabRoute,
        agentDefinitionTaskManagementTabRoute,
        agentDefinitionResourcesTabRoute,
        agentDefinitionPermissionsTabRoute,
        agentDefinitionVersionsTabRoute,
        agentDefinitionEvalsTabRoute.addChildren([
          agentDefinitionEvalsListRoute,
          agentDefinitionEvalRunDetailRoute,
        ]),
        agentDefinitionSessionsLayoutRoute.addChildren([
          agentDefinitionSessionsListRoute,
          agentRunDetailRoute,
        ]),
      ]),
    ]),
    workflowsListLayoutRoute.addChildren([
      workflowDefinitionsTabRoute,
      allWorkflowRunsRoute,
      workflowGroupsTabRoute,
      workflowGroupDetailRoute,
      workflowTemplatesRoute,
      workflowScheduledRerunsRoute,
      workflowStepTesterRoute,
      workflowTrafficGeneratorRoute,
      workflowDataAssetsRoute,
      workflowDataMappingsRoute,
      workflowDataMappingDetailRoute,
      workflowStatefulTablesRoute,
      workflowStatefulTableDetailRoute,
      workflowLayoutRoute.addChildren([
        workflowDefinitionOverviewRoute,
        workflowDefinitionStepsRoute,
        workflowDefinitionRunsRoute.addChildren([workflowRunDetailRoute]),
        workflowDefinitionVersionsRoute,
        workflowDefinitionTestRoute,
        workflowDefinitionAnalyticsRoute,
        workflowDefinitionApiTokensRoute,
        workflowDefinitionFormSubmissionsRoute,
        workflowManagerAgentRoute,
        workflowMemoriesRoute,
        workflowActivityRoute,
        workflowVersionLayoutRoute.addChildren([
          workflowVersionOverviewRoute,
          workflowVersionStepsRoute,
          workflowVersionRunsRoute,
          workflowVersionVersionsRoute,
          workflowVersionTestRoute,
          workflowVersionAnalyticsRoute,
        ]),
      ]),
    ]),

    quoteBuilderRoute,
    productCatalogRoute,
    customerProductMatchesRoute,
    supplierProductMatchesRoute,
    feedbackRoute,
    discountsRoute,
    discountsGroupReviewRoute,
    discountsV1Route,
    discountsV2RedirectRoute,
    discountsV2GroupRedirectRoute,
    priceListsRoute,
    sharedReportRoute,
    sharedLinksRoute,
    agentMonitorListingRoute,
    agentMonitorDetailRoute,
  ]),
]);
